import React, { useEffect, useState, useCallback, useRef, act, ChangeEvent } from "react";
import { useNavigate } from 'react-router-dom';
import SideNavbar from '../../../components/SideNavbar';
import { useAuth } from '../../../AuthContext';
import { useSubAgentAuth } from '../SubAgent/SubAgentAuthContext';
import TableSkeleton from '../../../components/TableSkeleton';

// Redux
import {
    setLeadProperty,
    setSearchString,
    clearLeadManagementFilterProps,
    clearLeadManagementPropsSearchString,
    clearLeadManagementAssigneeOptions
} from '../../../redux/leadManagementPropsSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';

// firebase
import { app, db, functions } from "../../../firebaseConfig";
import { doc, getDoc, collection, query, where, getDocs, Timestamp, limit, addDoc, updateDoc, writeBatch, getCountFromServer, DocumentData, documentId } from 'firebase/firestore';
import { getAuth } from "firebase/auth";

// Functions
import { fetchLeads, LeadData, addNewLead, updateLead } from '../Admin/Function/LeadFunctions'
import Toast from "../../../components/Toast";
import ActionModals from "../../../components/modals/ActionModals";
import getCurrentScreen from "../../../Helper/GetScreen";

export interface Lead {
    id: string;
    name: string;
    email: string;
    createdAt?: Date;
    followUpDate?: Date;
    number: string;
    assignedTo: string;
    adminNotes?: string;
    sourceId?: string;
    stageId?: string;
    stateId?: string;
    statusId?: string;
    distribution: number;
    isActive: boolean;
    isNewEntry?: boolean;
    followUpTime?: string;
    updatedAt?: Date;
    campaign?: string;
}

interface Student {
    id: string;
    name: string;
    createdAt: Timestamp;
    source: { source: string };
    stage: { color: string; priority: number; stage: string };
    priority: { color: string; priority: number; status: string };
    admissionStatus?: { admissionStatus: string };
}

interface User {
    id: string;
    name: string;
    lastName: string;
    email: string;
    distribution?: number;
    isAdmin: boolean;
}

interface ExcelRow {
    FirstName: string;
    LastName: string;
    Email?: string;
    PhoneNumber: string;
}

interface LeadUpdateData extends DocumentData {
    name: string;
    number: string;
    email: string;
    phoneNumber: string;
    assignedTo: string;
    adminNotes: string;
    sourceId: string;
    stageId: string;
    stateId: string;
    statusId: string;
    updatedAt: Timestamp;
    updatedBy: string;
    createdAt?: Timestamp;
    followUpDate?: Timestamp;
    followUpTime?: string;
}

interface ToastProps {
    message: string;
    isError?: boolean; // Make sure this is optional
    onClose: () => void;
    show: boolean;
}

interface LeadAdmissionStatus {
    admissionStatus: string;
}

interface LeadSource {
    source: string;
}

interface LeadStage {
    stage: string;
    priority: number;
    color: string;
}

interface LeadState {
    state: string;
    makeInactive: boolean;
}

interface LeadStatus {
    status: string;
    priority: number;
    color: string;
}

interface LeadWelcomeCallByAdmissionTeamStatus {
    welcomeCallByAdmissionTeamStatus: string;
}

interface LeadVisaFileProcessingStatus {
    visaFileProcessingStatus: string;
}

interface LeadApplicationMovedStatus {
    applicationMovedStatus: string;
}

interface LeadOfferLetterStatus {
    offerLetterStatus: string;
}

interface LeadFeePaymentStatus {
    feePaymentStatus: string;
}

interface LeadFileSubmissionStatus {
    fileSubmissionStatus: string;
}

interface LeadFollowUpStatus {
    followUpStatus: string;
}

interface LeadCampaign {
    campaign: string;
}

interface Destination {
    id: string
    name: string;
}

interface leadDateFilter {
    startDate: string;
    endDate: string;
}

interface DocumentWithId {
    id: string;
    data: any;
}

const LeadManagement = () => {


    const { currentUser, isAdmin, isManager, isStaff } = useAuth();
    const { currentSubAgent, subAgentData, subAgentPermissions } = useSubAgentAuth();
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    const dispatch = useAppDispatch();
    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);

    const selectedLeadValue = useAppSelector(state => state.leadManagementProps.leadStates);
    const selectedSourceValue = useAppSelector(state => state.leadManagementProps.leadSources);
    const selectedStageValue = useAppSelector(state => state.leadManagementProps.leadStages);
    const selectedStatusValue = useAppSelector(state => state.leadManagementProps.leadStatuses);
    const selectedAdmissionStatusValue = useAppSelector(state => state.leadManagementProps.leadAdmissionStatuses);
    const selectedWelcomeCallByAdmissionTeamStatusValue = useAppSelector(state => state.leadManagementProps.leadWelcomeCallByAdmissionTeamStatuses);
    const selectedVisaProcessingStatusValue = useAppSelector(state => state.leadManagementProps.leadVisaProcessingStatuses);
    const selectedApplicationMovedStatusValue = useAppSelector(state => state.leadManagementProps.leadApplicationMovedStatuses);
    const selectedConditionalOfferLetterStatusValue = useAppSelector(state => state.leadManagementProps.leadConditionalOfferLetterStatuses);
    const selectedUnConditionalOfferLetterStatusValue = useAppSelector(state => state.leadManagementProps.leadUnConditionalOfferLetterStatuses);
    const selectedFeePaymentStatusValue = useAppSelector(state => state.leadManagementProps.leadFeePaymentStatuses);
    const selectedFileSubmissionStatusValue = useAppSelector(state => state.leadManagementProps.leadFileSubmissionStatuses);
    const selectedFollowUpStatusValue = useAppSelector(state => state.leadManagementProps.leadFollowUpStatuses);
    const selectedAssigneeOptionsValue = useAppSelector(state => state.leadManagementProps.leadAssigneeOptions);
    const selectedCampaignOptionsValue = useAppSelector(state => state.leadManagementProps.leadCampaignOptions);
    const nameSearchString = useAppSelector(state => state.leadManagementProps.nameSearchString);
    const phoneSearchString = useAppSelector(state => state.leadManagementProps.phoneSearchString);
    const createdAtStartDateValue = useAppSelector(state => state.leadManagementProps.createdAtStartDate);
    const createdAtEndDateValue = useAppSelector(state => state.leadManagementProps.createdAtEndDate);

    const navigate = useNavigate();

    // collapse state for filter section
    const [isCollapsedFilter, setIsCollapsedFilter] = useState(true);

    // useEffect execution order control
    const [firstEffectDone, setFirstEffectDone] = useState(false);

    const [isPopupOpen, setPopupOpen] = useState(false);
    // Using pageSize for all pagination calculations
    const openPopup = () => {
        setEditingId(null);
        // For staff users in Staff screen, pre-fill assignedTo with current user ID
        if (isStaff && currentScreen === "Staff" && currentUser) {
            setNewLead({ assignedTo: currentUser.uid });
        } else {
            setNewLead({});
        }
        setPopupOpen(true);
    };
    const closePopup = () => {
        setPopupOpen(false);
    };
    const [newLead, setNewLead] = useState<Partial<LeadData>>({});
    const inputRef = useRef<HTMLInputElement>(null);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        const upperCased = input.value.toLocaleUpperCase();

        setNewLead((prev) => ({ ...prev, name: upperCased }));

        // Restore cursor after render
        requestAnimationFrame(() => {
            if (inputRef.current) {
                inputRef.current.setSelectionRange(start, end);
            }
        });
    };

    const [editingId, setEditingId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [stages, setStages] = useState<{ id: string; stage: string }[]>([]);
    const [states, setStates] = useState<{ id: string; state: string }[]>([]);
    const [statuses, setStatuses] = useState<{ id: string; status: string }[]>([]);
    const [assignedToOptions, setAssignedToOptions] = useState<User[]>([]);
    const auth = getAuth(app);
    const [sources, setSources] = useState<{ id: string; source: string }[]>([]);
    const [campaigns, setCampaigns] = useState<{ id: string; campaign: string }[]>([]);
    const [password, setPassword] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");

    // Filter states
    const [leadStates, setLeadStates] = useState<DocumentWithId[]>([]);
    const [leadStatuses, setLeadStatuses] = useState<DocumentWithId[]>([]);
    const [leadStages, setLeadStages] = useState<DocumentWithId[]>([]);
    const [leadSources, setLeadSources] = useState<DocumentWithId[]>([]);
    const [admissionStatuses, setAdmissionStatuses] = useState<DocumentWithId[]>([]);
    const [welcomeCallByAdmissionTeamStatuses, setWelcomeCallByAdmissionTeamStatuses] = useState<DocumentWithId[]>([]);
    const [visaProcessingStatuses, setVisaProcessingStatuses] = useState<DocumentWithId[]>([]);
    const [applicationMovedStatuses, setApplicationMovedStatuses] = useState<DocumentWithId[]>([]);
    const [conditionalofferLetterStatuses, setConditionalOfferLetterStatuses] = useState<DocumentWithId[]>([]);
    const [unConditionalofferLetterStatuses, setUnConditionalOfferLetterStatuses] = useState<DocumentWithId[]>([]);
    const [feePaymentStatuses, setFeePaymentStatuses] = useState<DocumentWithId[]>([]);
    const [fileSubmissionStatuses, setFileSubmissionStatuses] = useState<DocumentWithId[]>([]);
    const followUpStatuses: DocumentWithId[] = [
        { id: 'false', data: { followUpStatus: 'False' } },
        { id: 'true', data: { followUpStatus: 'True' } }
    ];
    const [assigneeOptions, setAssigneeOptions] = useState<DocumentWithId[]>([]);
    const [campaignOptions, setCampaignOptions] = useState<DocumentWithId[]>([]);
    const [managerTeamOptions, setManagerTeamOptions] = useState<string[]>([]);
    const [destinations, setDestinations] = useState<DocumentWithId[]>([]);

    // states for actions
    const [selectedUserDetails, setSelectedUserDetails] = useState<{ id: string, name: string }[]>([]);
    const [selectedStudent, setSelectedStudent] = useState<LeadData | null>(null);
    const [selectedStudentPhone, setSelectedStudentPhone] = useState<string>('');
    const [collaborators, setCollaborators] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<{ id: string, name: string }[]>([]);
    const [selectedTransferUser, setSelectedTransferUser] = useState<{ id: string, name: string } | null>(null);
    const [currentAssignedUser, setCurrentAssignedUser] = useState<{ id: string, name: string } | null>(null);
    const [students, setStudents] = useState<Student[]>([]);

    // loading states
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    // modals
    const [showCollaborateModal, setShowCollaborateModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);

    // selected filters
    const [selectedLeadStates, setSelectedLeadStates] = useState<string[]>([]);
    const [selectedLeadStatuses, setSelectedLeadStatuses] = useState<string[]>([]);
    const [selectedLeadStages, setSelectedLeadStages] = useState<string[]>([]);
    const [selectedLeadSources, setSelectedLeadSources] = useState<string[]>([]);
    const [selectedAdmissionStatuses, setSelectedAdmissionStatuses] = useState<string[]>([]);
    const [selectedWelcomeCallByAdmissionTeamStatuses, setSelectedWelcomeCallByAdmissionTeamStatuses] = useState<string[]>([]);
    const [selectedVisaProcessingStatuses, setSelectedVisaProcessingStatuses] = useState<string[]>([]);
    const [selectedApplicationMovedStatuses, setSelectedApplicationMovedStatuses] = useState<string[]>([]);
    const [selectedConditionalOfferLetterStatuses, setSelectedConditionalOfferLetterStatuses] = useState<string[]>([]);
    const [selectedUnConditionalOfferLetterStatuses, setSelectedUnConditionalOfferLetterStatuses] = useState<string[]>([]);
    const [selectedFeePaymentStatuses, setSelectedFeePaymentStatuses] = useState<string[]>([]);
    const [selectedFileSubmissionStatuses, setSelectedFileSubmissionStatuses] = useState<string[]>([]);
    const [selectedFollowUpStatuses, setSelectedFollowUpStatuses] = useState<string[]>([]);
    const [selectedAssigneeOptions, setSelectedAssigneeOptions] = useState<string[]>([]);
    const [selectedCampaignOptions, setSelectedCampaignOptions] = useState<string[]>([]);
    const [selectedStartDate, setSelectedStartDate] = useState<string[]>([]);
    const [selectedEndDate, setSelectedEndDate] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [phoneSearchQuery, setPhoneSearchQuery] = useState<string>('');

    // states for actions and operations
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [selectedLeadsForAction, setSelectedLeadsForAction] = useState<string[]>([]);
    const [selectCurrentPage, setSelectCurrentPage] = useState(false);
    const MAX_SELECTABLE_LEADS = 2000;
    const [selectedAction, setSelectedAction] = useState<number | null>(null);
    const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
    const [actionSearchQuery, setActionSearchQuery] = useState('');
    const [highlightedActionIndex, setHighlightedActionIndex] = useState(-1);


    // State variables for pagination
    const [pageSize, setPageSize] = useState<number>(() => {
        const storedPageSize = sessionStorage.getItem('pageSize');
        return storedPageSize ? parseInt(storedPageSize, 10) : 10;
    });
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalLeadsCount, setTotalLeadsCount] = useState<number>(0);
    const [hasMore, setHasMore] = useState(true);
    const [lastDocument, setLastDocument] = useState<{
        id: string;
        leadDate: Timestamp;
    } | null>(null);
    const totalPages = Math.ceil(totalLeadsCount / pageSize);

    // Effect to close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setActionDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Effect to handle keyboard navigation
    useEffect(() => {
        if (!actionDropdownOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            // Using a local function to filter actions to avoid referencing Actions before declaration
            const filteredOptions = Actions.filter(item =>
                item.name.toLowerCase().includes(actionSearchQuery.toLowerCase()) && item.active
            );

            if (filteredOptions.length === 0) return;

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    event.stopPropagation();
                    setHighlightedActionIndex(prev => {
                        const nextIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
                        // Scroll the highlighted item into view if needed
                        setTimeout(() => {
                            const highlightedElement = document.getElementById(`action-option-${nextIndex}`);
                            highlightedElement?.scrollIntoView({ block: 'nearest' });
                        }, 0);
                        return nextIndex;
                    });
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    event.stopPropagation();
                    setHighlightedActionIndex(prev => {
                        const nextIndex = prev > 0 ? prev - 1 : 0;
                        // Scroll the highlighted item into view if needed
                        setTimeout(() => {
                            const highlightedElement = document.getElementById(`action-option-${nextIndex}`);
                            highlightedElement?.scrollIntoView({ block: 'nearest' });
                        }, 0);
                        return nextIndex;
                    });
                    break;
                case 'Enter':
                    event.preventDefault();
                    event.stopPropagation();
                    if (highlightedActionIndex >= 0 && highlightedActionIndex < filteredOptions.length) {
                        handleAction(filteredOptions[highlightedActionIndex].id);
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation();
                    setActionDropdownOpen(false);
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [actionDropdownOpen, actionSearchQuery, highlightedActionIndex]);

    // Temporary states for actions
    const [tempAssignee, setTempAssignee] = useState<string[]>([]);

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedAction(null);
        setActionSearchQuery('');
    };

    // Reset highlighted index when search query changes
    useEffect(() => {
        setHighlightedActionIndex(actionSearchQuery ? 0 : -1);
    }, [actionSearchQuery]);

    // Reset search query when dropdown closes
    useEffect(() => {
        if (!actionDropdownOpen) {
            setActionSearchQuery('');
            setHighlightedActionIndex(-1);
        }
    }, [actionDropdownOpen]);

    const handleActionSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setActionSearchQuery(e.target.value);
        setHighlightedActionIndex(e.target.value ? 0 : -1);
    };

    const handleActionDropdownKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !actionDropdownOpen) {
            e.preventDefault();
            setActionDropdownOpen(true);
        }
    };

    const handleActionSearchInputKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();

        // Handle arrow key navigation directly in the search input
        const filteredOptions = getFilteredActions().filter(item => item.active);

        if (filteredOptions.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedActionIndex(prev => {
                const nextIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
                // Scroll the highlighted item into view if needed
                setTimeout(() => {
                    const highlightedElement = document.getElementById(`action-option-${nextIndex}`);
                    highlightedElement?.scrollIntoView({ block: 'nearest' });
                }, 0);
                return nextIndex;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedActionIndex(prev => {
                const nextIndex = prev > 0 ? prev - 1 : 0;
                // Scroll the highlighted item into view if needed
                setTimeout(() => {
                    const highlightedElement = document.getElementById(`action-option-${nextIndex}`);
                    highlightedElement?.scrollIntoView({ block: 'nearest' });
                }, 0);
                return nextIndex;
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedActionIndex >= 0 && highlightedActionIndex < filteredOptions.length) {
                handleAction(filteredOptions[highlightedActionIndex].id);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setActionDropdownOpen(false);
        }
    };

    const clearAllFilters = (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(clearLeadManagementFilterProps());
    };

    const clearSearch = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (tempAssignee?.length > 0) {
            dispatch(setLeadProperty({ key: 'leadAssigneeOptions', value: tempAssignee }));
        }
        dispatch(clearLeadManagementPropsSearchString());
    };

    useEffect(() => {
        if (searchQuery === '' && phoneSearchQuery === '') {
            handleSearch();
        } else {
            setIsCollapsedFilter(false);
        }
    }, [searchQuery, phoneSearchQuery]);

    // Effect to refetch data when page size changes
    useEffect(() => {
        // Reset to first page when page size changes
        setCurrentPage(1);
        // Clear existing data to prevent confusion when showing a new page size
        setLeadsData([]);
        // Reset pagination state
        setLastDocument(null);
        setHasMore(true);
        // Always fetch page 1 when page size changes
        fetchPageData(1);
    }, [pageSize]);

    // useEffect to set the redux state for the selected items
    useEffect(() => {
        setSelectedLeadSources(selectedSourceValue);
        setSelectedLeadStages(selectedStageValue);
        setSelectedLeadStates(selectedLeadValue);
        setSelectedLeadStatuses(selectedStatusValue);
        setSelectedAdmissionStatuses(selectedAdmissionStatusValue);
        setSelectedWelcomeCallByAdmissionTeamStatuses(selectedWelcomeCallByAdmissionTeamStatusValue);
        setSelectedVisaProcessingStatuses(selectedVisaProcessingStatusValue);
        setSelectedApplicationMovedStatuses(selectedApplicationMovedStatusValue);
        setSelectedConditionalOfferLetterStatuses(selectedConditionalOfferLetterStatusValue);
        setSelectedUnConditionalOfferLetterStatuses(selectedUnConditionalOfferLetterStatusValue);
        setSelectedFeePaymentStatuses(selectedFeePaymentStatusValue);
        setSelectedFileSubmissionStatuses(selectedFileSubmissionStatusValue);
        setSelectedFollowUpStatuses(selectedFollowUpStatusValue);
        setSelectedAssigneeOptions(selectedAssigneeOptionsValue);
        setSelectedCampaignOptions(selectedCampaignOptionsValue);
        setSelectedStartDate(createdAtStartDateValue);
        setSelectedEndDate(createdAtEndDateValue);
    }, [firstEffectDone, selectedSourceValue, selectedStageValue, selectedLeadValue,
        selectedStatusValue, selectedAdmissionStatusValue, selectedWelcomeCallByAdmissionTeamStatusValue,
        selectedVisaProcessingStatusValue, selectedApplicationMovedStatusValue, selectedConditionalOfferLetterStatusValue,
        selectedUnConditionalOfferLetterStatusValue, selectedFeePaymentStatusValue, selectedFileSubmissionStatusValue,
        selectedFollowUpStatusValue, selectedAssigneeOptionsValue, selectedCampaignOptionsValue,
        createdAtStartDateValue, createdAtEndDateValue]);

    useEffect(() => {
        setSearchQuery(nameSearchString[0] || '');
        setPhoneSearchQuery(phoneSearchString[0] || '');
    }, [nameSearchString, phoneSearchString]);

    const RenderSettingsBox = (
        title: string,
        type: string,
        items: DocumentWithId[],
        selectedItems: string[],
        reduxKey: string,
        getDisplayText: (item: any) => string,
        getColor?: (item: any) => string | undefined
    ) => {

        const dispatch = useAppDispatch();

        const dropdownRef = useRef<HTMLDivElement>(null);
        const [localDropdownOpen, setLocalDropdownOpen] = useState<boolean>(false);
        const [localSearchQuery, setLocalSearchQuery] = useState('');
        const [inputValue, setInputValue] = useState('');
        // Add state for the currently highlighted item index
        const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                    setLocalDropdownOpen(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, []);

        const filterItems = (items: DocumentWithId[], getDisplayText: (item: any) => string) => {
            if (!localSearchQuery) return items;
            return items.filter(item =>
                getDisplayText(item.data).toLowerCase().includes(localSearchQuery.toLowerCase())
            );
        };

        const handleLocalSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            const value = e.target.value;
            setInputValue(value);
            setLocalSearchQuery(value);
            setLocalDropdownOpen(true);
        }

        // For storing temporary date value before confirming
        const [tempDateValue, setTempDateValue] = useState<string>('');

        const handleSelection = (id: string) => {
            // For date type inputs, store the value temporarily
            if (type === 'date') {
                setTempDateValue(id);
                // Don't close dropdown or dispatch action yet for date type
            } else {
                // For non-date inputs, proceed as before
                dispatch(setLeadProperty({ key: reduxKey, value: [id] }));
                setLocalDropdownOpen(false);
                setLocalSearchQuery('');
                const selectedItem = items?.find(item => item?.id === id);
                setInputValue(selectedItem ? getDisplayText(selectedItem.data) : '');
            }
        };

        // Handle confirmation of date selection
        const handleDateConfirm = () => {
            if (tempDateValue) {
                dispatch(setLeadProperty({ key: reduxKey, value: [tempDateValue] }));
                setTempDateValue('');
                setLocalDropdownOpen(false);
            }
        };

        // Handle cancellation of date selection
        const handleDateCancel = () => {
            setTempDateValue('');
            setLocalDropdownOpen(false);
        };

        const getSelectedItemText = () => {
            if (selectedItems && selectedItems.length > 0) {
                const selectedItem = items?.find(item => item?.id === selectedItems[0]);
                return selectedItem ? getDisplayText(selectedItem.data) : '';
            }
            return inputValue;
        };

        const handleInputClick = () => {
            setLocalDropdownOpen(true);
            if (!inputValue && type !== 'date') {
                // Set the display text when initially selecting the input
                // For selected items, show the text as placeholder, but empty the input value to enable typing
                const selectedItem = items?.find(item => item?.id === selectedItems[0]);
                if (selectedItem) {
                    // Instead of setting input value, use placeholder or just clear for better UX
                    setInputValue('');
                    setLocalSearchQuery('');
                }
            }
        };

        const handleClear = (e: React.MouseEvent) => {
            e.stopPropagation();
            // setSelectedItems([]);
            dispatch(setLeadProperty({ key: reduxKey, value: [] }));
            setInputValue('');
            setLocalSearchQuery('');
            setTempDateValue(''); // Also clear the temporary date value
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
            const filteredItems = filterItems(items, getDisplayText);

            if (!localDropdownOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
                // Open dropdown if it's closed and user presses down arrow or enter
                e.preventDefault();
                setLocalDropdownOpen(true);
                return;
            }

            if (!localDropdownOpen) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setHighlightedIndex(prevIndex => {
                        if (prevIndex >= filteredItems.length - 1) {
                            return 0; // Cycle back to first item
                        }
                        return prevIndex + 1;
                    });
                    break;

                case 'ArrowUp':
                    e.preventDefault();
                    setHighlightedIndex(prevIndex => {
                        if (prevIndex <= 0) {
                            return filteredItems.length - 1; // Cycle to last item
                        }
                        return prevIndex - 1;
                    });
                    break;

                case 'Enter':
                    e.preventDefault();
                    if (highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
                        handleSelection(filteredItems[highlightedIndex].id);
                    } else if (type === 'date' && tempDateValue) {
                        handleDateConfirm();
                    }
                    break;

                case 'Escape':
                    e.preventDefault();
                    if (type === 'date' && tempDateValue) {
                        handleDateCancel();
                    } else {
                        setLocalDropdownOpen(false);
                    }
                    break;
            }
        };

        // Function to smoothly scroll highlighted item into view
        const scrollToHighlighted = (index: number) => {
            if (index < 0) return;

            setTimeout(() => {
                const highlightedElement = document.getElementById(`dropdown-item-${index}`);
                if (highlightedElement && dropdownRef.current) {
                    const dropdownList = dropdownRef.current.querySelector('.max-h-48');
                    if (dropdownList) {
                        const listRect = dropdownList.getBoundingClientRect();
                        const elementRect = highlightedElement.getBoundingClientRect();

                        if (elementRect.top < listRect.top) {
                            highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                        } else if (elementRect.bottom > listRect.bottom) {
                            highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                        }
                    }
                }
            }, 0);
        };

        // Call scrollToHighlighted when highlightedIndex changes
        useEffect(() => {
            scrollToHighlighted(highlightedIndex);
        }, [highlightedIndex]);

        return (
            <div ref={dropdownRef} className={`flex flex-col bg-white rounded-lg shadow-sm relative ${localDropdownOpen ? 'z-40' : 'z-0'}`}>
                <div className="px-2 py-1.5">
                    <label className="block text-[14px] font-medium text-gray-600 mb-0.5 font-Montserrat">{title}</label>
                    <div className="relative">
                        <div className="relative flex items-center">
                            <input
                                type={type}
                                className={`w-full h-8 px-2 py-1 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-Montserrat placeholder-gray-400 ${type === 'date' && selectedItems.length > 0 ? '[&::-webkit-calendar-picker-indicator]:hidden' : ''
                                    }`}
                                title={title}
                                value={type === 'date' ? (tempDateValue || selectedItems[0] || '') : (localDropdownOpen ? localSearchQuery : getSelectedItemText())}
                                placeholder="Select..."
                                onClick={handleInputClick}
                                onChange={(e) => {
                                    if (type === 'date') {
                                        handleSelection(e.target.value);
                                    } else {
                                        e.stopPropagation();
                                        setLocalSearchQuery(e.target.value);
                                        setInputValue(e.target.value);
                                        setLocalDropdownOpen(true);
                                    }
                                }}
                            />
                            <div className="absolute right-1.5 flex items-center space-x-0.5">
                                {(selectedItems.length > 0 || inputValue) && (
                                    <button
                                        onClick={handleClear}
                                        className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                                    >
                                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                                {type !== 'date' && (
                                    <button
                                        type="button"
                                        className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                                        onClick={() => setLocalDropdownOpen(!localDropdownOpen)}
                                    >
                                        <svg
                                            className={`w-4 h-4 text-gray-400 transform transition-transform duration-200 ${localDropdownOpen ? 'rotate-180' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {/* Date selection confirmation buttons */}
                {localDropdownOpen && type === 'date' && tempDateValue && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden z-40 p-2">
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={handleDateCancel}
                                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
                                Cancel
                            </button>
                            <button
                                onClick={handleDateConfirm}
                                className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors">
                                Confirm
                            </button>
                        </div>
                    </div>
                )}
                {localDropdownOpen && type !== 'date' && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden max-h-48 overflow-y-auto z-40 min-w-[200px]"
                    >
                        <div className="p-1">
                            {/* Search input for filtering */}
                            <div className="px-2 py-1 sticky top-0 bg-white z-10 border-b border-gray-100">
                                <input
                                    type="text"
                                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder={`Search ${title}...`}
                                    value={localSearchQuery}
                                    onChange={(e) => setLocalSearchQuery(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={handleKeyDown}
                                    autoFocus
                                />
                            </div>
                            {items.length === 0 && (
                                <div
                                    className={`flex items-center px-2 py-1.5 cursor-pointer rounded-md transition-colors`}
                                >
                                    <span
                                        className="text-xs font-Montserrat truncate flex-1"
                                    >
                                        No {title} available
                                    </span>
                                </div>
                            )}
                            {filterItems(items, getDisplayText).length > 0 ? (
                                filterItems(items, getDisplayText).map((item, index) => (
                                    <div
                                        id={`dropdown-item-${index}`}
                                        key={item?.id}
                                        className={`flex items-center px-2 py-1.5 cursor-pointer rounded-md transition-colors ${highlightedIndex === index
                                            ? 'bg-blue-100 text-blue-800'
                                            : selectedItems?.includes(item?.id)
                                                ? 'bg-blue-50 text-blue-700'
                                                : 'hover:bg-gray-50'
                                            }`}
                                        onClick={() => handleSelection(item?.id)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                    >
                                        <span
                                            className="text-xs font-Montserrat truncate flex-1"
                                            style={getColor ? { color: getColor(item?.data) } : undefined}
                                        >
                                            {getDisplayText(item?.data)}
                                        </span>
                                        {selectedItems?.includes(item?.id) && (
                                            <svg className="w-4 h-4 text-blue-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="px-2 py-1.5 text-xs text-gray-500 text-center">
                                    No results found
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const SettingsBoxesLayout: React.FC = () => {
        return (
            <div className="grid grid-cols-4 gap-3 p-2">
                {/* {RenderSettingsBox(
                    'Lead Sources',
                    'text',
                    leadSources,
                    selectedLeadSources,
                    'leadSources',
                    (item: LeadSource) => item?.source
                )}
                {RenderSettingsBox(
                    'Lead Stages',
                    'text',
                    leadStages,
                    selectedLeadStages,
                    'leadStages',
                    (item: LeadStage) => item?.stage,
                    (item: LeadStage) => item?.color
                )}
                {RenderSettingsBox(
                    'Lead States',
                    'text',
                    leadStates,
                    selectedLeadStates,
                    'leadStates',
                    (item: LeadState) => item?.state
                )}
                {RenderSettingsBox(
                    'Lead Status',
                    'text',
                    leadStatuses,
                    selectedLeadStatuses,
                    'leadStatuses',
                    (item: LeadStatus) => item?.status,
                    (item: LeadStatus) => item?.color
                )}
                {RenderSettingsBox(
                    'Admission Status',
                    'text',
                    admissionStatuses,
                    selectedAdmissionStatuses,
                    'leadAdmissionStatuses',
                    (item: LeadAdmissionStatus) => item?.admissionStatus
                )}
                {RenderSettingsBox(
                    'Welcome Call Status',
                    'text',
                    welcomeCallByAdmissionTeamStatuses,
                    selectedWelcomeCallByAdmissionTeamStatuses,
                    'leadWelcomeCallByAdmissionTeamStatuses',
                    (item: LeadWelcomeCallByAdmissionTeamStatus) => item?.welcomeCallByAdmissionTeamStatus
                )}
                {RenderSettingsBox(
                    'Visa Processing',
                    'text',
                    visaProcessingStatuses,
                    selectedVisaProcessingStatuses,
                    'leadVisaProcessingStatuses',
                    (item: LeadVisaFileProcessingStatus) => item?.visaFileProcessingStatus
                )}
                {RenderSettingsBox(
                    'Application Status',
                    'text',
                    applicationMovedStatuses,
                    selectedApplicationMovedStatuses,
                    'leadApplicationMovedStatuses',
                    (item: LeadApplicationMovedStatus) => item?.applicationMovedStatus
                )}
                {RenderSettingsBox(
                    'Conditional Offer Letter Status',
                    'text',
                    conditionalofferLetterStatuses,
                    selectedConditionalOfferLetterStatuses,
                    'leadConditionalOfferLetterStatuses',
                    (item: LeadOfferLetterStatus) => item?.offerLetterStatus
                )}
                {RenderSettingsBox(
                    'Unconditional Offer Letter Status',
                    'text',
                    unConditionalofferLetterStatuses,
                    selectedUnConditionalOfferLetterStatuses,
                    'leadUnConditionalOfferLetterStatuses',
                    (item: LeadOfferLetterStatus) => item?.offerLetterStatus
                )}
                {RenderSettingsBox(
                    'Fee Payment Status',
                    'text',
                    feePaymentStatuses,
                    selectedFeePaymentStatuses,
                    'leadFeePaymentStatuses',
                    (item: LeadFeePaymentStatus) => item?.feePaymentStatus
                )}
                {RenderSettingsBox(
                    'File Submission Status',
                    'text',
                    fileSubmissionStatuses,
                    selectedFileSubmissionStatuses,
                    'leadFileSubmissionStatuses',
                    (item: LeadFileSubmissionStatus) => item?.fileSubmissionStatus
                )}
                {currentScreen !== "Staff" && RenderSettingsBox(
                    'Assignee Options',
                    'text',
                    assigneeOptions,
                    selectedAssigneeOptions,
                    'leadAssigneeOptions',
                    (item: User) => item?.name
                )}
                {RenderSettingsBox(
                    'Follow Up Status',
                    'text',
                    followUpStatuses,
                    selectedFollowUpStatuses,
                    'leadFollowUpStatuses',
                    (item: LeadFollowUpStatus) => item?.followUpStatus
                )}
                {RenderSettingsBox(
                    'Campaign Options',
                    'text',
                    campaignOptions,
                    selectedCampaignOptions,
                    'leadCampaignOptions',
                    (item: LeadCampaign) => item?.campaign
                )} */}
                {RenderSettingsBox(
                    'Created From',
                    'date',
                    [],
                    selectedStartDate,
                    'createdAtStartDate',
                    (item: leadDateFilter) => item?.startDate
                )}
                {RenderSettingsBox(
                    'Created To',
                    'date',
                    [],
                    selectedEndDate,
                    'createdAtEndDate',
                    (item: leadDateFilter) => item?.endDate
                )}
            </div>
        );
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch admission statuses
                const admissionStatusesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadAdmissionStatus`));
                setAdmissionStatuses(admissionStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadAdmissionStatus
                })));

                // Fetch lead sources
                const leadSourcesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadSources`));
                setLeadSources(leadSourcesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadSource
                })));

                // Fetch lead stages
                const leadStagesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadStages`));
                setLeadStages(leadStagesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadStage
                })));

                // Fetch lead states
                const leadStatesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadStates`));
                setLeadStates(leadStatesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadState
                })));

                // Fetch lead statuses
                const leadStatusesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadStatuses`));
                setLeadStatuses(leadStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadStatus
                })));

                // Fetch welcome call statuses
                const welcomeCallByAdmissionTeamStatusesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadWelcomeCallByAdmissionTeamStatus`));
                setWelcomeCallByAdmissionTeamStatuses(welcomeCallByAdmissionTeamStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadWelcomeCallByAdmissionTeamStatus
                })));

                // Fetch visa processing statuses
                const visaProcessingStatusesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadVisaFileProcessingStatus`));
                setVisaProcessingStatuses(visaProcessingStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadVisaFileProcessingStatus
                })));

                // Fetch application moved statuses
                const applicationMovedStatusesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadApplicationMovedStatus`));
                setApplicationMovedStatuses(applicationMovedStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadApplicationMovedStatus
                })));

                // Fetch offer letter statuses
                const offerLetterStatusesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadOfferLetterStatus`));
                setConditionalOfferLetterStatuses(offerLetterStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadOfferLetterStatus
                })));
                setUnConditionalOfferLetterStatuses(offerLetterStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadOfferLetterStatus
                })));

                // Fetch fee payment statuses
                const feePaymentStatusesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadFeePaymentStatus`));
                setFeePaymentStatuses(feePaymentStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadFeePaymentStatus
                })));

                // Fetch file submission statuses
                const fileSubmissionStatusesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadFileSubmissionStatus`));
                setFileSubmissionStatuses(fileSubmissionStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadFileSubmissionStatus
                })));

                // fetch campaign options where isActive is true
                const campaignOptionsSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadCampaigns`));
                setCampaignOptions(campaignOptionsSnapshot.docs.filter(doc => doc.data().isActive).map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadCampaign
                })));

                const destinationsSnapshot = await getDocs(collection(db, 'countries'));
                setDestinations(destinationsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as Destination
                })));

                // Fetch assignee options
                let subAgentQuery = query(
                    collection(db, "users"),
                    where("companyId", "==", subAgentData?.companyId)
                );

                const usersSnapshot = await getDocs(subAgentQuery);
                const users = usersSnapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        data: {
                            name: data.name,
                            email: data.email || '',
                        }
                    };
                });
                setAssigneeOptions(users);
                setFirstEffectDone(true);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [subAgentData?.companyId]);

    // Get filtered actions for dropdown
    const getFilteredActions = () => {
        return Actions.filter(item =>
            item.name.toLowerCase().includes(actionSearchQuery.toLowerCase())
        );
    };

    // Actions array definition
    const Actions = [
        {
            id: 1,
            name: "Change Assignee",
            icon: "assign",
            color: "text-blue-500",
            active: true,
            action: () => { }
        },
        {
            id: 2,
            name: "Change Status",
            icon: "change-status",
            color: "text-yellow-500",
            active: true,
            action: () => { }
        },
        {
            id: 3,
            name: "Change Stage",
            icon: "change-stage",
            color: "text-green-500",
            active: true,
            action: () => { }
        },
        {
            id: 4,
            name: "Change State",
            icon: "change-state",
            color: "text-orange-500",
            active: true,
            action: () => { }
        },
        {
            id: 5,
            name: "Add Follow Up",
            icon: "add-follow-up",
            color: "text-blue-500",
            active: true,
            action: () => { }
        },
        {
            id: 6,
            name: "Change ProgramType",
            icon: "change-programType",
            color: "text-yellow-500",
            active: true,
            action: () => { }
        },
        {
            id: 7,
            name: "Change Intake",
            icon: "change-intake",
            color: "text-green-500",
            active: true,
            action: () => { }
        },
        {
            id: 8,
            name: "Change Stream",
            icon: "change-stream",
            color: "text-blue-500",
            active: true,
            action: () => { }
        },
        {
            id: 9,
            name: "Change Country",
            icon: "change-country",
            color: "text-green-500",
            active: true,
            action: () => { }
        },
        {
            id: 10,
            name: "Mark as closed",
            icon: "mark-as-closed",
            color: "text-red-500",
            active: true,
            action: () => { }
        }
    ];

    // functon to get action permission
    const getActionPermission = (action: string) => {
        const actions = subAgentPermissions?.actionPermission || [];
        const actionPermission = Array.isArray(actions) ? actions.find((item: any) => item.name === action) : null;
        return actionPermission ? actionPermission.allowed : false;
    }

    const handleAction = async (actionId: number) => {
        if (selectedLeadsForAction.length === 0) {
            setToastMessage("Please select leads to perform this action");
            setShowToast(true);
            return;
        }

        switch (actionId) {
            case 1:
                // Assign
                setSelectedAction(1);
                setActionDropdownOpen(false);
                break;
            case 2:
                // Change Status
                setSelectedAction(2);
                setActionDropdownOpen(false);
                break;
            case 3:
                // Change Stage
                setSelectedAction(3);
                setActionDropdownOpen(false);
                break;
            case 4:
                // Change State
                setSelectedAction(4);
                setActionDropdownOpen(false);
                break;
            case 5:
                // Add Follow Up
                setSelectedAction(5);
                setActionDropdownOpen(false);
                break;
            case 6:
                // Change ProgramType
                setSelectedAction(6);
                setActionDropdownOpen(false);
                break;
            case 7:
                // Change Intake
                setSelectedAction(7);
                setActionDropdownOpen(false);
                break;
            case 8:
                // Change Stream
                setSelectedAction(8);
                setActionDropdownOpen(false);
                break;
            case 9:
                // Chaneg Country
                setSelectedAction(9);
                setActionDropdownOpen(false);
                break;
            case 10:
                // Mark as closed
                setSelectedAction(10);
                setActionDropdownOpen(false);
                break;
            default:
                break;
        }
    }

    const handleSelectAll = () => {
        if (selectCurrentPage) {
            // Unselect all leads on the current page
            const currentPageLeadIds = leadsData.map(lead => lead.id);
            const remainingSelectedLeads = selectedLeadsForAction.filter(id => !currentPageLeadIds.includes(id));
            setSelectedLeadsForAction(remainingSelectedLeads);
            setSelectCurrentPage(false);
        } else {
            // Select all leads on the current page, but check maximum limit
            const currentPageLeadIds = leadsData.map(lead => lead.id);
            const existingSelections = selectedLeadsForAction.filter(id => !currentPageLeadIds.includes(id));
            const newSelections = [...existingSelections, ...currentPageLeadIds];

            if (newSelections.length > MAX_SELECTABLE_LEADS) {
                setToastMessage(`Failed: Selection limit exceeded. Maximum: ${MAX_SELECTABLE_LEADS}, attempting: ${newSelections.length}`);
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
                return;
            }

            setSelectedLeadsForAction(newSelections);
            setSelectCurrentPage(true);
        }
    };

    const handleLeadSelection = (leadId: string) => {
        // Check if the lead is already selected
        if (selectedLeadsForAction.includes(leadId)) {
            // If it was selected, remove it
            setSelectedLeadsForAction(selectedLeadsForAction.filter(id => id !== leadId));

            // If we're removing a lead from the current page, update selectCurrentPage state
            if (leadsData.some(lead => lead.id === leadId)) {
                setSelectCurrentPage(false);
            }
        } else {
            // If it wasn't selected, check the maximum limit before adding
            if (selectedLeadsForAction.length >= MAX_SELECTABLE_LEADS) {
                setToastMessage(`Failed: Selection limit reached. Maximum of ${MAX_SELECTABLE_LEADS} leads allowed.`);
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
                return;
            }

            // Add the lead to selected items
            setSelectedLeadsForAction([...selectedLeadsForAction, leadId]);

            // Check if all leads on the current page are now selected
            const allCurrentPageLeadsSelected = leadsData.every(lead =>
                lead.id === leadId || selectedLeadsForAction.includes(lead.id)
            );

            if (allCurrentPageLeadsSelected) {
                setSelectCurrentPage(true);
            }
        }
    }

    const handleOnClose = async (message: string) => {
        setSelectCurrentPage(false);
        setSelectedLeadsForAction([]);
        setSelectedAction(null);
        fetchPageData(currentPage);
        if (message !== "") {
            setToastMessage(message);
            setShowToast(true);
        }
    }

    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const statesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadStates`));
                const statusesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadStatuses`));
                const stagesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadStages`));
                const sourcesSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadSources`));
                const campaignsSnapshot = await getDocs(collection(db, `companies/${subAgentData?.companyId}/leadCampaigns`));
                const fetchedStages = stagesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    stage: doc.data().stage,
                }));
                const fetchedStates = statesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    state: doc.data().state,
                }));
                const fetchedStatuses = statusesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    status: doc.data().status,
                }));
                const fetchedSources = sourcesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    source: doc.data().source,
                }));
                const fetchedCampaigns = campaignsSnapshot.docs.filter(doc => doc.data().isActive).map((doc) => ({
                    id: doc.id,
                    campaign: doc.data().campaign,
                }));
                setSources(fetchedSources);
                setStates(fetchedStates);
                setStatuses(fetchedStatuses);
                setStages(fetchedStages);
                setCampaigns(fetchedCampaigns);
            } catch (error) {
                console.error("Error fetching dropdown data:", error);
            }
        };
        fetchDropdownData();
    }, []);

    const fetchAssignedToOptions = useCallback(async () => {
        if (!currentSubAgent?.companyId) return;
        try {
            const companyId = currentSubAgent?.companyId;

            let usersQuery = query(
                collection(db, "users"),
                where("companyId", "==", companyId)
            );

            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                    id: doc.id,
                    name: data.name,
                    lastName: data.lastName || '',
                    email: data.email || '',
                    distribution: data.distribution || 0,
                    isAdmin: data.isAdmin || false,
                };
            });
            const distributedUsers = distributeEqually(users);
            setAssignedToOptions(distributedUsers);
        }
        catch (error) {
            console.error("Error fetching users:", error);
        }
    }, [currentSubAgent?.companyId]);

    useEffect(() => { fetchAssignedToOptions(); }, [currentSubAgent?.companyId, fetchAssignedToOptions]);

    // Check if number already exists in the database
    const checkNumberExists = async (number: string, excludeLeadId?: string) => {
        if (!currentSubAgent || !number) return false;

        try {
            const leadsRef = collection(db, `companies/${currentSubAgent?.companyId}/leads`);
            let q = query(leadsRef, where("number", "==", number));

            const querySnapshot = await getDocs(q);

            // If we're editing a lead, exclude the current lead from duplicate check
            if (excludeLeadId) {
                return querySnapshot.docs.some(doc => doc.id !== excludeLeadId);
            }

            return !querySnapshot.empty;
        } catch (error) {
            console.error("Error checking number existence:", error);
            return false;
        }
    };

    const handleAddLead = async () => {

        if (!newLead.name || !newLead.number || !newLead.assignedTo) {
            alert("Name, number, and assigned to are required");
            return;
        }

        if (newLead.number.replace(/\D/g, '').length <= 10) {
            setToastMessage("Error: Please enter a valid number by including the country code. Example: +91XXXXXX1234");
            setShowToast(true);
            setTimeout(() => {
                setShowToast(false);
            }, 3000);
            return;
        } else {
            // remove all spaces and characters from the number
            newLead.number = newLead.number.replace(/\D/g, '');

            // then add + to the number
            newLead.number = "+" + newLead.number;
        }

        // Check if the number already exists
        const numberExists = await checkNumberExists(newLead.number);
        if (numberExists) {
            setToastMessage("Lead Already Exists with this Number");
            setShowToast(true);
            setTimeout(() => {
                setShowToast(false);
            }, 3000);
            return;
        }

        const assignedTo = newLead?.assignedTo ? newLead?.assignedTo : "";
        console.log("Assigned to:", assignedTo);

        const subAgentSource = sources?.find(s => s.source === "Sub Agent")?.id || "";

        const subAgent = currentSubAgent?.uid || "";

        try {
            const addedLead = await addNewLead(db, auth.currentUser, {
                name: newLead?.name,
                number: newLead?.number,
                reversedNumber: newLead?.number.split('').reverse().join(''),
                email: newLead?.email || "",
                assignedTo: assignedTo,
                subAgentId: subAgent,
                adminNotes: newLead?.adminNotes || "",
                sourceId: subAgentSource,
                stageId: newLead?.stageId || "",
                stateId: newLead?.stateId || "",
                statusId: newLead?.statusId || "",
                campaignId: newLead?.campaignId || "",
                admissionStatusId: newLead?.admissionStatusId || "",
                isActive: true,
                hasActiveFollowUp: false,
                activeFollowUpId: null,
                updatedAt: Timestamp.fromDate(new Date()),
                createdAt: Timestamp.fromDate(new Date()),
                pipelines: newLead.pipelines || [],
                destinationId: newLead.destinationId || "",
                intakeMonth: newLead.intakeMonth || "",
                intakeYear: newLead.intakeYear || 0,
                preferredStreamId: newLead.preferredStreamId || "",
                fileSubmissionStatusId: newLead.fileSubmissionStatusId || "",
                feePaymentStatusId: newLead.feePaymentStatusId || "",
                conditionalOfferLetterStatusId: newLead.conditionalOfferLetterStatusId || "",
                unconditionalOfferLetterStatusId:  newLead.unconditionalOfferLetterStatusId || "",
                applicationMovedStatusId: newLead.applicationMovedStatusId || "",
                visaFileProcessingStatusId: newLead.visaFileProcessingStatusId || "",
                welcomeCallByAdmissionTeamStatusId: newLead.welcomeCallByAdmissionTeamStatusId || "",
                subAdmissionStatusId: newLead.subAdmissionStatusId || "",
                subStatusId: newLead.subStatusId || "",
            }, { companyId: currentSubAgent?.companyId });

            // Add the new lead to the 0th position
            setLeadsData((prevLeads) => [{
                ...addedLead
            }, ...prevLeads]);

            setTotalLeadsCount((prevCount) => prevCount + 1);

            // Show success toast
            setToastMessage("Lead added successfully!");
            setShowToast(true);

            // Automatically close popup
            setPopupOpen(false);
            setNewLead({});
            setEditingId(null);

            // Hide toast after 3 seconds
            setTimeout(() => {
                setShowToast(false);
            }, 3000);
        } catch (error) {
            console.error("Error adding lead:", error);
            setToastMessage(`Error adding lead: ${(error as Error).message}`);
            setShowToast(true);
        }
    };

    const handleEditLead = async (id: string) => {
        const leadToEdit = leadsData?.find((lead) => lead.id === id);
        if (leadToEdit) {
            setEditingId(id);
            setNewLead({
                name: leadToEdit.name,
                email: leadToEdit.email,
                number: leadToEdit.number,
                assignedTo: leadToEdit.assignedTo,
                subAgentId: leadToEdit.subAgentId,
                sourceId: leadToEdit.sourceId || "",
                adminNotes: leadToEdit.adminNotes || "",
                followUp: leadToEdit.followUp,
                createdAt: leadToEdit.createdAt,
                stageId: leadToEdit.stageId,
                stateId: leadToEdit.stateId,
                statusId: leadToEdit.statusId,
                campaignId: leadToEdit.campaignId,
            });
            setPopupOpen(true);
            setToastMessage("Lead opened for editing");
            setShowToast(true);

            setTimeout(() => {
                setShowToast(false);
            }, 3000);

        }
    };

    function convertToDate(value: Date | Timestamp): Date {
        if (value instanceof Timestamp) {
            return value.toDate(); // Convert Timestamp to Date
        }
        return new Date(value); // Handle number or other Date-compatible types
    }

    const handleUpdateLead = async () => {
        // For staff users in Staff screen, auto-assign to themselves
        if (isStaff && currentScreen === "Staff" && currentUser) {
            newLead.assignedTo = currentUser.uid;
        }

        if (!editingId || !newLead.name || !newLead.number || !newLead.assignedTo) {
            alert("Name, number, and assigned to are required");
            return;
        }

        if (newLead.number.replace(/\D/g, '').length <= 10) {
            setToastMessage("Error: Please enter a valid number by including the country code. Example: +91XXXXXX1234");
            setShowToast(true);
            setTimeout(() => {
                setShowToast(false);
            }, 3000);
            return;
        } else {
            // remove all spaces and characters from the number
            newLead.number = newLead.number.replace(/\D/g, '');

            // then add + to the number
            newLead.number = "+" + newLead.number;
        }

        // Check if the number already exists (excluding the current lead being edited)
        const numberExists = await checkNumberExists(newLead.number, editingId);
        if (numberExists) {
            setToastMessage("Lead Already Exists with this Number");
            setShowToast(true);
            setTimeout(() => {
                setShowToast(false);
            }, 3000);
            return;
        }

        try {
            const updatedLead = await updateLead(db, auth.currentUser, editingId, {
                name: newLead.name,
                number: newLead.number,
                reversedNumber: newLead.number.split('').reverse().join(''),
                email: newLead.email || "",
                assignedTo: newLead.assignedTo || "",
                subAgentId: newLead.subAgentId || "",
                adminNotes: newLead.adminNotes || "",
                sourceId: newLead.sourceId || "",
                stageId: newLead.stageId || "",
                stateId: newLead.stateId || "",
                statusId: newLead.statusId || "",
                campaignId: newLead.campaignId || "",
                admissionStatusId: newLead.admissionStatusId || "",
                pipelines: newLead.pipelines || [],
                updatedAt: Timestamp.fromDate(new Date()),
                createdAt: newLead.createdAt ? newLead.createdAt : Timestamp.fromDate(new Date()),
                isActive: true,
                destinationId: newLead.destinationId || "",
                intakeMonth: newLead.intakeMonth || "",
                intakeYear: newLead.intakeYear || 0,
                preferredStreamId: newLead.preferredStreamId || "",
                fileSubmissionStatusId: newLead.fileSubmissionStatusId || "",
                feePaymentStatusId: newLead.feePaymentStatusId || "",
                conditionalOfferLetterStatusId: newLead.conditionalOfferLetterStatusId || "",
                unconditionalOfferLetterStatusId: newLead.unconditionalOfferLetterStatusId || "",
                applicationMovedStatusId: newLead.applicationMovedStatusId || "",
                visaFileProcessingStatusId: newLead.visaFileProcessingStatusId || "",
                welcomeCallByAdmissionTeamStatusId: newLead.welcomeCallByAdmissionTeamStatusId || "",
                subAdmissionStatusId: newLead.subAdmissionStatusId || "",
                subStatusId: newLead.subStatusId || "",
            }, { companyId: currentSubAgent?.companyId });

            setLeadsData((prevLeads) =>
                prevLeads.map((lead) =>
                    lead.id === editingId ? {
                        ...lead,
                        name: updatedLead.name,
                        email: updatedLead.email,
                        number: updatedLead.number,
                        reversedNumber: updatedLead.reversedNumber,
                        assignedTo: updatedLead.assignedTo,
                        subAgentId: updatedLead.subAgentId,
                        adminNotes: updatedLead.adminNotes,
                        sourceId: updatedLead.sourceId,
                        stageId: updatedLead.stageId,
                        stateId: updatedLead.stateId,
                        statusId: updatedLead.statusId,
                        campaignId: updatedLead.campaignId,
                        updatedAt: updatedLead.updatedAt,
                        isActive: updatedLead.isActive,
                    } : lead
                )
            );

            setPopupOpen(false);
            setNewLead({});
            setEditingId(null);

            setToastMessage("Lead updated successfully");
            setShowToast(true);

            setTimeout(() => {
                setShowToast(false);
            }, 3000);

        } catch (error) {
            console.error("Error updating lead:", error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            setToastMessage(`Error updating lead: ${errorMessage}`);
            setShowToast(true);

            setTimeout(() => {
                setShowToast(false);
            }, 3000);
        }
    };

    const handleToggleActive = async (id: string, isActive: boolean) => {
        const leadRef = doc(db, `companies/${currentSubAgent?.companyId}/leads`, id);
        try {
            await updateDoc(leadRef, { isActive: !isActive });
            setLeadsData(
                leadsData.map((lead) =>
                    lead.id === id ? { ...lead, isActive: !isActive } : lead
                )
            );
            setToastMessage(isActive ? "Lead deactivated" : "Lead reactivated");
            setShowToast(true);

            setTimeout(() => {
                setShowToast(false);
            }, 3000);
        } catch (error) {
            console.error("Error toggling lead active state:", error);
            setToastMessage("Error changing lead status");
            setShowToast(true);

            setTimeout(() => {
                setShowToast(false);
            }, 3000);
        }
    };

    const getStatusCircleColor = (status: string) => {
        switch (status) {
            case 'Low Priority':
                return 'bg-red-300';
            case 'Meduim Priority':
                return 'bg-blue-200';
            case 'High Priority':
                return 'bg-black';
            default:
                return 'bg-transperant';
        }
    };

    const distributeEqually = (users: any[]) => {
        const equalShare = Math.floor(100 / users.length);
        const remainder = 100 % users.length;
        return users.map((user, index) => ({
            ...user,
            distribution: index < remainder ? equalShare + 1 : equalShare,
        }));
    };

    const handleSearchUsers = async (value: string) => {
        if (!subAgentData?.companyId || value.length < 3) {
            setSearchResults([]);
            return;
        }

        setIsLoadingUsers(true);
        try {
            const upperValue = value.toUpperCase();
            const usersRef = collection(db, 'users');
            const q = query(
                usersRef,
                where('companyId', '==', subAgentData.companyId),
                where('name', '>=', upperValue),
                where('name', '<=', upperValue + '\uf8ff'),
                limit(10)
            );

            const querySnapshot = await getDocs(q);
            const usersData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name
            }));

            setSearchResults(usersData);
        } catch (error) {
            console.error('Error searching users:', error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const fetchExistingCollaborators = async (student: LeadData) => {
        if (!subAgentData?.companyId) return;

        try {
            // First, get the lead document to get collaborator IDs
            const leadRef = doc(db, `companies/${subAgentData.companyId}/leads`, student.id);
            const leadDoc = await getDoc(leadRef);
            if (leadDoc.exists()) {
                const leadData = leadDoc.data();
                const existingCollaborators = leadData.collaborators || [];
                setCollaborators(existingCollaborators);

                // Then, fetch user details for each collaborator
                if (existingCollaborators.length > 0) {
                    const userPromises = existingCollaborators.map(async (userId: string) => {
                        const userDoc = await getDoc(doc(db, 'users', userId));
                        if (userDoc.exists()) {
                            return {
                                id: userDoc.id,
                                name: userDoc.data().name
                            };
                        }
                        return null;
                    });

                    const users = await Promise.all(userPromises);
                    const validUsers = users.filter((user): user is { id: string, name: string } => user !== null);
                    setSelectedUserDetails(validUsers);
                }
            }
        } catch (error) {
            console.error('Error fetching existing collaborators:', error);
        }
    };

    const handleSelectUser = async (userId: string) => {
        if (!collaborators.includes(userId)) {
            // Add to collaborators array
            setCollaborators([...collaborators, userId]);

            // Add to selected user details
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
                const subAgentData = userDoc.data();
                setSelectedUserDetails(prev => [...prev, {
                    id: userId,
                    name: subAgentData.name
                }]);
            }
        }
    };

    const handleRemoveUser = (userId: string) => {
        setCollaborators(collaborators.filter(id => id !== userId));
        setSelectedUserDetails(prev => prev.filter(user => user.id !== userId));
    };

    // Fetch available users with same companyId
    const fetchAvailableUsers = async () => {
        if (!subAgentData?.companyId) return;

        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('companyId', '==', subAgentData.companyId));
            const querySnapshot = await getDocs(q);

            const usersData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name
            }));

            setSearchResults(usersData);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    useEffect(() => {
        if (showCollaborateModal) {
            fetchAvailableUsers();
        }
    }, [showCollaborateModal]);

    const handleCollaborateSubmit = async () => {
        if (!selectedStudent || !subAgentData?.companyId) return;

        try {
            const leadRef = doc(db, `companies/${subAgentData.companyId}/leads`, selectedStudent.id);
            await updateDoc(leadRef, {
                collaborators: collaborators,
                collaborateDate: Timestamp.fromDate(new Date()), 
            });
            setToastMessage('Collaborators added successfully!');
            setShowToast(true);
            setShowCollaborateModal(false);
            setCollaborators([]);
            setSelectedUserDetails([]);
            setSelectedStudent(null);
            setSearchTerm('');
            setSearchResults([]);
        } catch (error) {
            setToastMessage('Failed to add collaborators. Please try again.');
            setShowToast(true);
            console.error('Error updating collaborators:', error);
        }
    };

    const handleSelectTransferUser = (user: { id: string, name: string }) => {
        setSelectedTransferUser(user);
    };

    const handleTransferSubmit = async () => {
        if (!selectedStudent || !subAgentData?.companyId || !selectedTransferUser) return;

        try {
            // Find the lead document of this specific student
            const leadRef = doc(db, `companies/${subAgentData.companyId}/leads`, selectedStudent.id);

            // get the activeFollowUpId of this specific student
            const leadDoc = await getDoc(leadRef);
            const leadData = leadDoc?.data();
            const activeFollowUpId = leadData?.activeFollowUpId;



            // if activeFollowUpId is present, find the followUp document of this specific student
            const followUpRef = activeFollowUpId ? doc(db, `companies/${subAgentData.companyId}/followUps`, activeFollowUpId) : null;


            // Update assignedTo field of this specific student
            await updateDoc(leadRef, {
                assignedTo: selectedTransferUser.id
            });

            // if activeFollowUpId is present, update userId field of this specific student's followUp document
            if (followUpRef) {
                await updateDoc(followUpRef, {
                    userId: selectedTransferUser.id
                });
            }

            // Update local state based on user type
            if (isAdmin && currentScreen === 'Admin') {
                // For admin, update the lead's assignedTo in the local state
                setStudents(prevStudents =>
                    prevStudents.map(student =>
                        student.id === selectedStudent.id
                            ? { ...student, assignedTo: selectedTransferUser.id }
                            : student
                    )
                );
            } else {
                // For normal users, remove the transferred lead from the local state
                setStudents(prevStudents =>
                    prevStudents.filter(student => student.id !== selectedStudent.id)
                );
            }

            setToastMessage('Lead transferred successfully!');
            setShowToast(true);
            setShowTransferModal(false);
            setSelectedTransferUser(null);
            setSelectedStudent(null);
            setSearchTerm('');
            setSearchResults([]);
        } catch (error) {
            setToastMessage('Failed to transfer lead. Please try again.');
            setShowToast(true);
            console.error('Error transferring lead:', error);
        }
    };

    const handleStudentClick = (studentId: string) => {
        navigate(`/subagent/studentdetails/${studentId}`);
    };

    const handleCollaborateClick = async (student: LeadData) => {
        // Clear previous state
        setSelectedUserDetails([]);
        setCollaborators([]);
        setSearchTerm('');
        setSearchResults([]);

        setSelectedStudent(student);
        if (subAgentData?.companyId) {
            try {
                const leadRef = doc(db, `companies/${subAgentData.companyId}/leads`, student.id);
                const leadDoc = await getDoc(leadRef);
                if (leadDoc.exists()) {
                    const leadData = leadDoc.data();
                    setSelectedStudentPhone(leadData.number || 'No number available');
                }
                // Fetch existing collaborators
                await fetchExistingCollaborators(student);
            } catch (error) {
                console.error('Error fetching student data:', error);
                setSelectedStudentPhone('No number available');
            }
        }
        setShowCollaborateModal(true);
    };

    const handleTransferClick = async (student: LeadData) => {
        setSelectedStudent(student);
        setSelectedTransferUser(null);
        setSearchTerm('');
        setSearchResults([]);
        setCurrentAssignedUser(null);

        if (subAgentData?.companyId) {
            try {
                const leadRef = doc(db, `companies/${subAgentData.companyId}/leads`, student.id);
                const leadDoc = await getDoc(leadRef);
                if (leadDoc.exists()) {
                    const leadData = leadDoc.data();
                    setSelectedStudentPhone(leadData.number || 'No number available');

                    // Fetch current assigned user's name
                    if (leadData.assignedTo) {
                        const userDoc = await getDoc(doc(db, 'users', leadData.assignedTo));
                        if (userDoc.exists()) {
                            setCurrentAssignedUser({
                                id: userDoc.id,
                                name: userDoc.data().name
                            });
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching student data:', error);
                setSelectedStudentPhone('No number available');
            }
        }
        setShowTransferModal(true);
    };

    const [leadsData, setLeadsData] = useState<LeadData[]>([]);
    // We're using server-side pagination, so we don't need to slice the data



    // Handle page size change
    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        sessionStorage.setItem('pageSize', newPageSize.toString());

        // Clear existing data to prevent confusion when showing a new page size
        setLeadsData([]);
        // Reset pagination state
        setLastDocument(null);
        setHasMore(true);

        // Store the current page in session storage
        sessionStorage.setItem('currentPage', JSON.stringify(1));
    };

    const handlePageChange = (pageNumber: number) => {
        if (pageNumber < 1 || pageNumber > totalPages || (pageNumber > currentPage && !hasMore)) {
            return;
        }

        setCurrentPage(pageNumber);
        fetchPageData(pageNumber);

        // store the current page in session storage
        sessionStorage.setItem('currentPage', pageNumber.toString());
        sessionStorage.setItem('cursorId', lastDocument?.id || '');
    };

    // Helper function to fetch data for a specific page
    const fetchPageData = (pageNum: number) => {

        console.log("createdAtStartDate:", selectedStartDate);

        // Always fetch new data when changing pages
        // This ensures we're getting the correct data for the page
        fetchLeadsdata(false, pageNum, {
            selectedLeadSources: selectedLeadSources,
            selectedLeadStates: selectedLeadStates,
            selectedLeadStages: selectedLeadStages,
            selectedLeadStatuses: selectedLeadStatuses,
            selectedAdmissionStatuses: selectedAdmissionStatuses,
            selectedWelcomeCallByAdmissionTeamStatuses: selectedWelcomeCallByAdmissionTeamStatuses,
            selectedVisaProcessingStatuses: selectedVisaProcessingStatuses,
            selectedApplicationMovedStatuses: selectedApplicationMovedStatuses,
            selectedConditionalOfferLetterStatuses: selectedConditionalOfferLetterStatuses,
            selectedUnConditionalOfferLetterStatuses: selectedUnConditionalOfferLetterStatuses,
            selectedFeePaymentStatuses: selectedFeePaymentStatuses,
            selectedFileSubmissionStatuses: selectedFileSubmissionStatuses,
            selectedFollowUpStatuses: selectedFollowUpStatuses,
            selectedCampaignOptions: selectedCampaignOptions,
            searchQuery: searchQuery,
            phoneSearchQuery: phoneSearchQuery,
            selectedStartDate: selectedStartDate,
            selectedEndDate: selectedEndDate,
        });
    };

    const getAssignedToValue = () => {
        if (currentScreen === "Staff" && currentUser?.uid) {
            return [currentUser.uid];
        }

        // if (currentSubAgent && currentSubAgent?.uid) {
        //     return [currentSubAgent.uid];
        // }

        if (currentScreen === "Manager" && selectedAssigneeOptions.length === 0 && managerTeamOptions?.length > 0) {
            return managerTeamOptions;
        }

        if (selectedAssigneeOptions.length > 0) {
            return selectedAssigneeOptions;
        }

        return undefined; // or return undefined if you want to skip the query constraint
    };


    async function fetchLeadsdata(
        isLoadingMore = false,
        pageNum?: number,
        overrideFilters?: {
            selectedLeadSources?: any[];
            selectedLeadStates?: any[];
            selectedLeadStages?: any[];
            selectedLeadStatuses?: any[];
            selectedAdmissionStatuses?: any[];
            selectedWelcomeCallByAdmissionTeamStatuses?: any[];
            selectedVisaProcessingStatuses?: any[];
            selectedApplicationMovedStatuses?: any[];
            selectedConditionalOfferLetterStatuses?: any[];
            selectedUnConditionalOfferLetterStatuses?: any[];
            selectedFeePaymentStatuses?: any[];
            selectedFileSubmissionStatuses?: any[];
            selectedFollowUpStatuses?: any[];
            selectedSubAgentOptions?: any[];
            selectedCampaignOptions?: any[];
            selectedPrefferedStreams?: any[];
            selectedSuperAgents?: any[];
            selectedAdmissionSubStatus?: any[];
            selectedLeadSubSources?: any[];
            selectedLeadSubStages?: any[];
            selectedLeadSubStatuses?: any[];
            selectedDestinations?: any[];
            searchQuery?: string;
            phoneSearchQuery?: string;
            selectedStartDate?: any[];
            selectedEndDate?: any[];
            selectedTransferredDates?: any[];
            selectedTransferredEndDates?: any[];
            selectedUpdatedAtStartDate?: any[];
            selectedUpdatedAtEndDate?: any[];
            selectedIntakeYear?: any[];
            selectedIntakeMonth?: any[];
        }
    ) {
        // Only prevent duplicate load-more requests
        if (isLoadingMore && loading) return;

        setLoading(true);
        const pageToFetch = pageNum !== undefined ? pageNum : currentPage;

        try {
            const startTime = new Date();
            const assignedTo = await getAssignedToValue();
            const auth = getAuth();

            if (!auth.currentUser) {
                console.error('User not logged in');
                return;
            }

            // Cursor-based pagination logic
            let direction: 'first' | 'next' | 'prev' = 'first';
            let cursorId: string | undefined = undefined;

            if (pageToFetch === 1) {
                direction = 'first';
            } else if (pageToFetch > currentPage) {
                direction = 'next';
                cursorId = lastDocument?.id;
            } else if (pageToFetch < currentPage) {
                direction = 'prev';
                cursorId = leadsData[0]?.id;
            }

            if (!cursorId && pageToFetch > 1) {
                cursorId = sessionStorage.getItem('cursorId') || '';
            }

            console.log("Fetching leads data for page:", cursorId);

            // Use override values if provided, otherwise use current state
            const filters = overrideFilters || {};

            const result = await fetchLeads(db, auth.currentUser, {
                subAgentId: currentSubAgent?.uid,
                pageSize: pageSize,
                direction,
                cursorId,
                // Only apply isActive filter if explicitly needed
                isActive: undefined, // Removed Staff-specific filter here
                sourceIds: filters.selectedLeadSources ?? selectedLeadSources,
                stateIds: filters.selectedLeadStates ?? selectedLeadStates,
                stageIds: filters.selectedLeadStages ?? selectedLeadStages,
                statusIds: filters.selectedLeadStatuses ?? selectedLeadStatuses,
                admissionStatusIds: filters.selectedAdmissionStatuses ?? selectedAdmissionStatuses,
                welcomeCallStatusIds: filters.selectedWelcomeCallByAdmissionTeamStatuses ?? selectedWelcomeCallByAdmissionTeamStatuses,
                visaProcessStatusIds: filters.selectedVisaProcessingStatuses ?? selectedVisaProcessingStatuses,
                applicationStatusIds: filters.selectedApplicationMovedStatuses ?? selectedApplicationMovedStatuses,
                conditionalOfferLetterStatusIds: filters.selectedConditionalOfferLetterStatuses ?? selectedConditionalOfferLetterStatuses,
                unconditionalOfferLetterStatusIds: filters.selectedUnConditionalOfferLetterStatuses ?? selectedUnConditionalOfferLetterStatuses,
                feePaymentStatusIds: filters.selectedFeePaymentStatuses ?? selectedFeePaymentStatuses,
                fileSubmissionStatusIds: filters.selectedFileSubmissionStatuses ?? selectedFileSubmissionStatuses,
                activeFollowUpIds: filters.selectedFollowUpStatuses ?? selectedFollowUpStatuses,
                // Handle Staff assignment differently
                assignedTo: isStaff && currentScreen === "Staff" ? [currentUser?.uid].filter((id): id is string => !!id) : assignedTo,
                campaignIds: filters.selectedCampaignOptions ?? selectedCampaignOptions,
                searchValue: (filters.searchQuery ?? searchQuery).length > 2 ? (filters.searchQuery ?? searchQuery) : '',
                phoneValue: (filters.phoneSearchQuery ?? phoneSearchQuery).length > 2 ? (filters.phoneSearchQuery ?? phoneSearchQuery) : '',
                startDate: (filters.selectedStartDate ?? selectedStartDate).length > 0 ? convertToFirebaseTimestamp((filters.selectedStartDate ?? selectedStartDate)[0], true) : null,
                endDate: (filters.selectedEndDate ?? selectedEndDate).length > 0 ? convertToFirebaseTimestamp((filters.selectedEndDate ?? selectedEndDate)[0], false) : null,
                orderByField: "createdAt"
            }, {
                companyId: subAgentData?.companyId
            });


            const response = result.data;
            setLeadsData(response?.leads || []);
            setHasMore(response?.hasMore);
            setLastDocument(response?.lastDocument);
            setTotalLeadsCount(response?.count);

            // Check if we should update the selectCurrentPage state based on current selections
            const currentPageLeadIds = response?.leads.map(lead => lead.id) || [];
            if (currentPageLeadIds.length > 0) {
                const allCurrentPageLeadsSelected = currentPageLeadIds.every(id =>
                    selectedLeadsForAction.includes(id)
                );
                setSelectCurrentPage(allCurrentPageLeadsSelected);
            }

            if (response?.leads.length === 0) {
                setToastMessage("No leads found for the selected filters");
                setShowToast(true);
                setTimeout(() => {
                    setShowToast(false);
                }, 3000);
            }

            const endTime = new Date();
            const timeDiff = endTime.getTime() - startTime.getTime();
            console.log("Time taken to fetch leads data:", timeDiff, "ms");
        } catch (error) {
            console.error('Failed to fetch leads:', error);
            setToastMessage('Failed to fetch leads. Please try again.');
            setShowToast(true);
        } finally {
            setLoading(false);
        }
    }

    const handleCopy = async () => {
        if (!password) return;

        try {
            await navigator.clipboard.writeText(password);
            setIsCopied(true);

            // Reset the "Copied" state after 2 seconds
            setTimeout(() => {
                setIsCopied(false);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy text:', err);
        }
    };

    const handleGeneratePassword = () => {
        // Add your password generation logic here

    };

    const handleSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        dispatch(setSearchString({ key: 'nameSearchString', value: [e.target.value] }));
    }

    const handleSearchPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneSearchQuery(e.target.value);
        dispatch(setSearchString({ key: 'phoneSearchString', value: [e.target.value] }));
    }

    const handleSearch = (
        e?: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLImageElement, MouseEvent> | React.MouseEvent<Element, MouseEvent>
    ) => {
        // For any search action, reset to page 1
        setCurrentPage(1);

        // If called manually (without an event), directly fetch data
        if (!e) {
            fetchPageData(1);
            return;
        }

        // Handle keyboard event (Enter key)
        if ("key" in e && e.key === "Enter") {
            e.preventDefault(); // Prevent form submission
            fetchPageData(1);
            return;
        }

        // Handle mouse click event
        if (e.type === "click") {
            fetchPageData(1);
        }

        if (selectedAssigneeOptions.length !== 0) {
            setTempAssignee(selectedAssigneeOptions);
            // set all the filters to empty
            dispatch(clearLeadManagementAssigneeOptions());
        }

    };

    // Initial load
    useEffect(() => {
        if (firstEffectDone) {
            // Always start with page 1 when filters change
            setCurrentPage(1);
            fetchPageData(1);
        }
    }, [firstEffectDone, managerTeamOptions, selectedLeadSources, selectedLeadStates, selectedLeadStages, selectedLeadStatuses,
        selectedAdmissionStatuses, selectedWelcomeCallByAdmissionTeamStatuses, selectedVisaProcessingStatuses,
        selectedApplicationMovedStatuses, selectedConditionalOfferLetterStatuses, selectedUnConditionalOfferLetterStatuses,
        selectedFeePaymentStatuses, selectedFileSubmissionStatuses, selectedFollowUpStatuses, selectedAssigneeOptions, selectedCampaignOptions,
        selectedStartDate, selectedEndDate, pageSize]);

    // Function to load the next page
    const loadMore = () => {
        if (!hasMore || loading || currentPage >= totalPages) return;

        // Go to the next page
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        fetchPageData(nextPage);
    };

    const showToastMessage = (message: string, isError: boolean = false) => {
        setToastMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    const getTotalFilters = () => {
        const filterCount =
            selectedLeadSources.length +
            selectedLeadStates.length +
            selectedLeadStages.length +
            selectedLeadStatuses.length +
            selectedAdmissionStatuses.length +
            selectedWelcomeCallByAdmissionTeamStatuses.length +
            selectedVisaProcessingStatuses.length +
            selectedApplicationMovedStatuses.length +
            selectedConditionalOfferLetterStatuses.length +
            selectedUnConditionalOfferLetterStatuses.length +
            selectedFeePaymentStatuses.length +
            selectedFileSubmissionStatuses.length +
            selectedFollowUpStatuses.length +
            selectedAssigneeOptions.length +
            selectedCampaignOptions.length +
            selectedStartDate.length +
            selectedEndDate.length;

        return filterCount > 0 ? filterCount : 0;
    };


    return (
        <div className="min-h-screen bg-gray-200">
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300 w-[calc(100%-${isCollapsed ? '70px' : '290px'})]`}>
                <div className="bg-white p-6 rounded-3xl shadow-md">
                    <div className="flex items-center gap-6 mb-8">
                        <div className="flex items-center justify-center w-14 h-14 rounded-full" style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)' }}>
                            <img src={`${process.env.PUBLIC_URL}/images/desktop/ph_student.svg`} alt="Lead Icon" />
                        </div>
                        <h2 className="text-2xl font-medium text-black"> Lead Management </h2>
                        <div className="ml-auto flex items-center gap-4">
                            {/* <div ref={dropdownRef} className={`flex flex-col bg-white rounded-lg shadow-sm relative ${actionDropdownOpen ? '' : 'z-0'} w-[200px]`}>
                                <div className="relative">
                                    <div className="relative flex items-center text-black font-semibold rounded-full gap-2">
                                        <div
                                            className="w-full px-4 py-3 border border-gray-200 rounded-full flex items-center gap-2 text-xs 
                                            focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent  
                                            transition-all duration-200 font-Montserrat placeholder-gray-400 cursor-pointer"
                                            onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
                                            onKeyDown={handleActionDropdownKeyDown}
                                            tabIndex={0}
                                            role="combobox"
                                            aria-expanded={actionDropdownOpen}
                                            aria-haspopup="listbox"
                                            aria-controls="action-listbox"
                                        >
                                            {selectedAction ? Actions.find((item) => item.id === selectedAction)?.name : "Select action.."}
                                        </div>
                                        <div className="absolute right-1.5 flex items-center space-x-0.5 ">
                                            {selectedAction && (
                                                <button
                                                    onClick={handleClear}
                                                    className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                                                    aria-label="Clear selection"
                                                >
                                                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className={`p-0.5 hover:bg-gray-100 rounded-full transition-colors ${actionDropdownOpen ? 'rotate-180' : ''}`}
                                                onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
                                                aria-label={actionDropdownOpen ? "Close dropdown" : "Open dropdown"}
                                            >
                                                <svg
                                                    className={`w-4 h-4 text-gray-400 transform transition-transform duration-200`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                {actionDropdownOpen && (
                                    <div
                                        className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden max-h-48 min-w-[200px] w-full z-[9]"
                                        id="action-listbox"
                                        role="listbox"
                                    >
                                        <div className="p-2 sticky top-0 bg-white border-b border-gray-100 z-10">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="Search actions..."
                                                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    value={actionSearchQuery}
                                                    onChange={handleActionSearchInputChange}
                                                    onKeyDown={handleActionSearchInputKeyDown}
                                                    autoFocus
                                                    autoComplete="off"
                                                    spellCheck="false"
                                                    aria-label="Search actions"
                                                />
                                                {actionSearchQuery && (
                                                    <button
                                                        onClick={() => setActionSearchQuery('')}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                                                        aria-label="Clear search"
                                                    >
                                                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="overflow-y-auto max-h-36 p-1">
                                            {getFilteredActions().length > 0 ? (
                                                getFilteredActions().map((item, index) => (
                                                    <div
                                                        id={`action-option-${index}`}
                                                        key={item?.id}
                                                        className={`flex items-center px-2 py-1.5 cursor-pointer rounded-md transition-colors ${highlightedActionIndex === index ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                                        onClick={() => item?.active ? handleAction(item?.id) : null}
                                                        onMouseEnter={() => setHighlightedActionIndex(index)}
                                                        role="option"
                                                        aria-selected={selectedAction === item.id}
                                                        tabIndex={-1}
                                                    >
                                                        <span
                                                            className={`text-xs font-Montserrat truncate flex-1 ${!item?.active ? 'pointer-events-none opacity-50' : ''} ${highlightedActionIndex === index ? 'text-blue-600' : ''}`}
                                                            aria-disabled={!item?.active}
                                                        >
                                                            {item?.name}
                                                        </span>
                                                        {selectedAction === item.id && (
                                                            <svg className="w-4 h-4 text-blue-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="px-2 py-4 text-xs text-gray-500 text-center">
                                                    No results found
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div> */}
                            {getActionPermission('Add') && (
                                <button onClick={openPopup} className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
                                    style={{ background: 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)', }}  >
                                    <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px', }}>    Add Lead    </span>
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Staff Icon" className="w-4 h-4 ml-2" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Search inputs */}
                    <div className="flex items-center gap-4 mt-4">
                        <div className="flex flex-col">
                            <div className="flex items-center w-80 rounded-3xl bg-zinc-100">
                                <input
                                    type="text"
                                    placeholder="Search for Student..."
                                    className="w-full px-4 py-2 text-gray-700 bg-zinc-100 focus:outline-none rounded-3xl"
                                    value={searchQuery}
                                    onChange={(e) => handleSearchString(e)}
                                    onKeyPress={handleSearch}
                                />
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`}
                                    alt="search-lens-icon"
                                    className="w-6 h-6 mr-2 cursor-pointer"
                                    onClick={(e) => handleSearch(e)}
                                />
                            </div>
                            {searchQuery && searchQuery.length < 3 && (
                                <span id='warning' className="text-xs text-red-500 font-normal mt-1 ml-2">Need atleast 3 characters</span>
                            )}
                        </div>
                        <div className="flex flex-col">
                            <div className="flex items-center w-80 rounded-3xl bg-zinc-100">
                                <input
                                    type="text"
                                    placeholder="Search by Number..."
                                    className="w-full px-4 py-2 text-gray-700 bg-zinc-100 focus:outline-none rounded-3xl"
                                    value={phoneSearchQuery}
                                    onChange={(e) => handleSearchPhone(e)}
                                    onKeyPress={handleSearch}
                                />
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`}
                                    alt="search-lens-icon"
                                    className="w-6 h-6 mr-2 cursor-pointer"
                                    onClick={(e) => handleSearch(e)}
                                />
                            </div>
                            {phoneSearchQuery && phoneSearchQuery.length < 3 && (
                                <span id='warning' className="text-xs text-red-500 font-normal mt-1 ml-2">Need atleast 3 characters</span>
                            )}
                        </div>
                        {(searchQuery || phoneSearchQuery) && (
                            <a
                                href="#"
                                className="text-xs text-blue-500 font-normal"
                                onClick={(e) => {
                                    e.preventDefault();
                                    clearSearch(e);
                                }}
                            >
                                Clear Search
                            </a>
                        )}
                    </div>

                    {/* collapsible filter settings */}
                    <div className={`mt-4 mb-8 border border-gray-200 rounded-lg relative z-[5] p-4 ${(searchQuery || phoneSearchQuery) && 'pointer-events-none opacity-40'}`}>
                        <div
                            className="flex items-center cursor-pointer justify-between"
                            onClick={() => setIsCollapsedFilter(!isCollapsedFilter)}
                        >
                            <span className="flex flex-col mr-2 font-semibold">Filters</span>

                            {searchQuery || phoneSearchQuery ? (
                                <h3>Filter not allowed while search</h3>
                            ) : null}
                            <span className="flex items-center gap-2">
                                {getTotalFilters() > 0 && (
                                    <a
                                        href="#"
                                        className="text-xs text-blue-500 font-normal mr-2"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            clearAllFilters(e);
                                        }}
                                    >
                                        Clear All Filters
                                    </a>
                                )}

                                <span className="text-xs text-gray-500 font-normal mr-6">
                                    {getTotalFilters()} Filters Applied
                                </span>

                                <span
                                    className="text-sm transition-transform duration-300 ease-in-out"
                                    style={{ transform: isCollapsedFilter ? 'rotate(0deg)' : 'rotate(180deg)' }}
                                >
                                    ▲
                                </span>
                            </span>
                        </div>

                        <div
                            className={`transition-all duration-300 ease-in-out transform origin-top ${isCollapsedFilter
                                ? 'max-h-[1000px] opacity-100 scale-y-100'
                                : 'overflow-hidden max-h-0 opacity-0 scale-y-95'
                                }`}
                        >
                            <div className="pt-4">
                                <SettingsBoxesLayout />
                            </div>
                        </div>
                    </div>


                    {/* Pagination Control - Top */}
                    {/* Pagination Control - Top */}
                    <div className="flex items-center justify-between mt-6 mb-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center">
                                <label className="text-xs text-gray-500 mr-2">Rows per page:</label>
                                <select
                                    value={pageSize}
                                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                    className="border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={500}>500</option>
                                </select>
                            </div>
                            <div className="ml-2 relative">
                                {/* Customize columns button */}
                            </div>
                        </div>

                        {/* Navigation buttons */}
                        <div className="flex-1 flex items-right justify-end gap-2">
                            {/* First page */}
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={loading || currentPage === 1}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &lt;&lt;
                            </button>

                            {/* Previous page */}
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={loading || currentPage === 1}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &lt;
                            </button>

                            {/* Current page */}
                            <div className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff]">
                                {currentPage}
                            </div>

                            {/* Next page */}
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={loading || !hasMore || (currentPage === Math.ceil(totalLeadsCount / pageSize))}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &gt;
                            </button>
                        </div>

                        {/* Page count */}
                        <div className="flex-1 text-right">
                            <div className="text-xs text-gray-500">
                                {loading ? 'Loading...' : `(Total: ${totalLeadsCount} Leads) ${currentPage} / ${totalPages} pages`}
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl overflow-hidden shadow-lg">
                        <div className="overflow-x-auto relative">
                            {loading ? (
                                <TableSkeleton columns={10} rows={pageSize} />
                            ) : (
                                <table className="min-w-full table-auto border-collapse">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-[#02385E] to-[#02385E]/90">
                                            {/* <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                <input
                                                    type="checkbox"
                                                    className="form-checkbox h-3.5 w-3.5 accent-cyan-600 rounded"
                                                    checked={selectCurrentPage}
                                                    onChange={() => handleSelectAll()}
                                                />
                                            </th> */}
                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                SL.No
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                Name
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                Created At
                                            </th>
                                            {/* <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                Source
                                            </th> */}
                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                Assigned To
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                Destination
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                Stage
                                            </th>

                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                Admission Status
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                Intake
                                            </th>
                                            {/* <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                Stage
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                Priority
                                            </th>
                                            <th className="px-4 py-3 text-sm font-semibold text-white tracking-wider whitespace-nowrap border-r border-[#ffffff20]">
                                                Follow Up
                                            </th> */}
                                            <th className="px-4 py-3 text-sm font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] min-w-[200px]">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {loading ? (
                                            Array(10).fill(0).map((_, index) => (
                                                <tr key={index} className={`transition-colors duration-150 ease-in-out ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                                                    {Array(9).fill(0).map((_, colIndex) => (
                                                        <td key={colIndex} className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100">
                                                            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            leadsData.map((lead, index) => (
                                                <tr key={lead.id} style={{ color: lead.isNewEntry ? 'black' : 'inherit' }}
                                                    className={`
                                            transition-colors duration-150 ease-in-out
                                            ${index % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-slate-50 hover:bg-blue-50'}
                                          `}
                                                >
                                                    {/* <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                        <div className="flex items-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedLeadsForAction.includes(lead.id)}
                                                                onChange={() => { handleLeadSelection(lead.id) }}
                                                                className="form-checkbox h-3.5 w-3.5 accent-cyan-600 rounded"
                                                            />
                                                        </div>
                                                    </td> */}
                                                    <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">{(currentPage - 1) * pageSize + index + 1}</td>
                                                    <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                        {lead.name}
                                                        {/* {lead.isActive === false && (
                                                            <span className="text-xs bg-red-100 text-red-800 ml-2 py-1 px-2 rounded">
                                                                Inactive
                                                            </span>
                                                        )} */}
                                                    </td>
                                                    <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                        {lead.createdAt
                                                            ? new Date(convertToDate(lead.createdAt)).toLocaleDateString("en-US", {
                                                                month: "short",
                                                                day: "numeric",
                                                                year: "numeric",
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                            })
                                                            : ""}
                                                    </td>
                                                    {/* <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                        {sources.find((src) => src.id === lead.sourceId)?.source ||
                                                            (lead.isNewEntry ? "--" :
                                                                (lead.sourceId ? "--" : "--"))}
                                                    </td> */}
                                                    <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                        {assigneeOptions?.find((assigneeOption) => assigneeOption.id === lead.assignedTo)?.data?.name || "--"}
                                                    </td>
                                                    <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                        {destinations.find((destination) => destination.id === lead.destinationId)?.data?.name || "--"}
                                                    </td>
                                                    <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                        {leadStages?.find((stage) => stage.id === lead.stageId)?.data?.stage || "--"}
                                                    </td>

                                                    <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                        {admissionStatuses?.find((admissionStatus) => admissionStatus.id === lead.admissionStatusId)?.data?.admissionStatus || "--"}
                                                    </td>
                                                    <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                        {lead.intakeMonth ? lead.intakeMonth + ',' : '-'}{lead.intakeMonth && lead.intakeYear !== 0 ? lead.intakeYear : '-'}
                                                    </td>


                                                    {/* <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                        {stages.find((stage) => stage.id === lead.stageId)?.stage || "--"}
                                                    </td>
                                                    <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`w-3 h-3 rounded-full ${getStatusCircleColor(statuses.find((status) => status.id === lead.statusId)?.status || "--")}`}></span>
                                                            {statuses.find((status) => status.id === lead.statusId)?.status || "--"}
                                                        </div>
                                                    </td>
                                                    <td className="p-2 text-center">
                                                        {lead?.followUp ? (
                                                            <span
                                                                className="text-xs font-semibold text-[#3483BE] cursor-pointer"
                                                            // title={lead.followUp}
                                                            >
                                                                {lead.followUp ?
                                                                    new Date(convertToDate(lead.followUp)).toLocaleDateString("en-US", {
                                                                        month: "short",
                                                                        day: "numeric",
                                                                        year: "numeric",
                                                                        hour: "2-digit",
                                                                        minute: "2-digit",
                                                                    })
                                                                    : "--"}

                                                            </span>
                                                        ) : (
                                                            "--"
                                                        )}
                                                    </td> */}

                                                    <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100">
                                                        <div className="flex items-center justify-center gap-3 min-w-[200px]">
                                                            {getActionPermission('Delete') && (
                                                                <button className="p-1 hover:bg-gray-100 rounded">
                                                                    <img
                                                                        src={lead.isActive ? `${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg` : `${process.env.PUBLIC_URL}/images/desktop/reseticon.svg`}
                                                                        alt={lead.isActive ? "Delete Icon" : "Reset Icon"}
                                                                        className="w-5 h-5 cursor-pointer"
                                                                        onClick={() => handleToggleActive(lead.id, lead.isActive)}
                                                                    />
                                                                </button>
                                                            )}
                                                            {getActionPermission('Edit') && (
                                                                <button className="p-1 hover:bg-gray-100 rounded">
                                                                    <img
                                                                        src={`${process.env.PUBLIC_URL}/images/desktop/editicon.svg`}
                                                                        alt="Edit Icon"
                                                                        onClick={() => handleEditLead(lead.id)}
                                                                        className="w-5 h-5 cursor-pointer"
                                                                    />
                                                                </button>
                                                            )}
                                                            {getActionPermission('Details') && (
                                                                <button className="p-1 hover:bg-gray-100 rounded">
                                                                    <img
                                                                        src={`${process.env.PUBLIC_URL}/images/desktop/print-preview.svg`}
                                                                        alt="preview-icon"
                                                                        className="w-5 h-5 cursor-pointer"
                                                                        onClick={() => handleStudentClick(lead.id)}
                                                                    />
                                                                </button>
                                                            )}
                                                            {getActionPermission('Collaborate') && (
                                                                <button className="p-1 hover:bg-gray-100 rounded">
                                                                    <img
                                                                        src={`${process.env.PUBLIC_URL}/images/desktop/collaborate.svg`}
                                                                        alt="collaborate-icon"
                                                                        className="w-5 h-5 cursor-pointer"
                                                                        onClick={() => handleCollaborateClick(lead)}
                                                                    />
                                                                </button>
                                                            )}
                                                            {getActionPermission('Transfer') && (
                                                                <button className="p-1 hover:bg-gray-100 rounded">
                                                                    <img
                                                                        src={`${process.env.PUBLIC_URL}/images/desktop/TransferLead.svg`}
                                                                        alt="transfer-lead-icon"
                                                                        className="w-5 h-5 cursor-pointer"
                                                                        onClick={() => handleTransferClick(lead)}
                                                                    />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                    {/* Pagination Control - Bottom */}
                    {/* Pagination Control - Top */}
                    <div className="flex items-center justify-between mt-6 mb-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center">
                                <label className="text-xs text-gray-500 mr-2">Rows per page:</label>
                                <select
                                    value={pageSize}
                                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                    className="border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={500}>500</option>
                                </select>
                            </div>
                            <div className="ml-2 relative">
                                {/* Customize columns button */}
                            </div>
                        </div>

                        {/* Navigation buttons */}
                        <div className="flex-1 flex items-right justify-end gap-2">
                            {/* First page */}
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={loading || currentPage === 1}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &lt;&lt;
                            </button>

                            {/* Previous page */}
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={loading || currentPage === 1}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &lt;
                            </button>

                            {/* Current page */}
                            <div className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff]">
                                {currentPage}
                            </div>

                            {/* Next page */}
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={loading || !hasMore || (currentPage === Math.ceil(totalLeadsCount / pageSize))}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &gt;
                            </button>
                        </div>

                        {/* Page count */}
                        <div className="flex-1 text-right">
                            <div className="text-xs text-gray-500">
                                {loading ? 'Loading...' : `(Total: ${totalLeadsCount} Leads) ${currentPage} / ${totalPages} pages`}
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* Add and Update  Modal */}
            {isPopupOpen && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative">
                        <div className=" top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/ph_student.svg`} alt="Staff Icon" />
                                </div>
                                <h2 className="text-xl font-semibold text-black">{editingId ? "Edit Lead" : "Add Lead"}</h2>
                            </div>
                            <div className="text-right">
                                <button onClick={closePopup} className="absolute top-4 right-4 font-semibold text-3xl" style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent', }}>
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" /></button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-6 mt-8">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    placeholder="Name"
                                    value={newLead.name}
                                    onChange={handleChange}
                                    className="border p-2 w-full mb-2"
                                    style={{ borderRadius: "24px", border: "1px solid #3483BE" }}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-6 mt-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">{`Assigned To ${currentScreen === 'Staff' ? '(myself)' : ''}`} <span className="text-red-500">*</span></label>
                                    <select
                                        value={newLead.assignedTo || ""}
                                        onChange={(e) => {
                                            const selectedUserId = e.target.value;
                                            setNewLead({ ...newLead, assignedTo: selectedUserId });
                                        }}
                                        className={`border p-2 w-full mb-2 ${isStaff && currentScreen === 'Staff' ? 'bg-gray-200 appearance-none cursor-not-allowed font-semibold' : ''}`}
                                        disabled={isStaff && currentScreen === 'Staff'}
                                        style={{
                                            borderRadius: '24px', border: '1px solid #3483BE',
                                        }}>
                                        {currentScreen !== 'Staff' && (<option value="">Assign To</option>)}
                                        {assignedToOptions.length > 0 ? (
                                            assignedToOptions
                                                .filter((user) => user.isAdmin === true)
                                                .map((user) => (
                                                    <option key={user.id} value={user.id}>
                                                        {user.name}
                                                    </option>
                                                ))
                                        ) : (
                                            <option disabled>No users available</option>
                                        )}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Email </label>
                                    <input
                                        type="text"
                                        placeholder="Email"
                                        value={newLead.email || ""}
                                        onChange={(e) =>
                                            setNewLead({ ...newLead, email: e.target.value })
                                        }
                                        className="border p-2 w-full mb-2"
                                        style={{ borderRadius: '24px', border: '1px solid #3483BE', }} />
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-6 mt-4">

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Phone <span className="text-red-600">*</span></label>
                                <input
                                    type="text"
                                    placeholder="Phone Number"
                                    value={newLead.number || ""}
                                    onChange={(e) =>
                                        setNewLead({ ...newLead, number: e.target.value })
                                    }
                                    className="border p-2 w-full mb-2"
                                    style={{ borderRadius: '24px', border: '1px solid #3483BE', }} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Source</label>
                                <select
                                    value={newLead.sourceId || ""}
                                    onChange={(e) => {
                                        const selectedSourceId = e.target.value;
                                        setNewLead({ ...newLead, sourceId: selectedSourceId });
                                    }}
                                    className="border p-2 w-full mb-2"
                                    style={{
                                        borderRadius: '24px',
                                        border: '1px solid #3483BE',
                                    }}
                                    disabled={true}
                                >
                                    {sources.length > 0 ? (
                                        sources
                                            .filter((source) => source.source === "Sub Agent")
                                            .map((source) => (
                                                <option key={source.id} value={source.id}>
                                                    {source.source}
                                                </option>
                                            ))
                                    ) : (
                                        <option disabled>No sources available</option>
                                    )}
                                </select>
                            </div>
                            {/* <div>
                                <label className="block text-sm font-medium text-gray-700">Campaign</label>
                                <select
                                    value={newLead.campaignId || ""}
                                    onChange={(e) => {
                                        const selectedCampaignId = e.target.value;
                                        setNewLead({ ...newLead, campaignId: selectedCampaignId });
                                    }}
                                    className="border p-2 w-full mb-2"
                                    style={{
                                        borderRadius: '24px',
                                        border: '1px solid #3483BE',
                                    }}
                                >
                                    <option value="">Select Campaign</option>
                                    {campaigns.length > 0 ? (
                                        campaigns.map((campaign) => (
                                            <option key={campaign.id} value={campaign.id}>
                                                {campaign.campaign}
                                            </option>
                                        ))
                                    ) : (
                                        <option disabled>No campaign available</option>
                                    )}
                                </select>
                            </div> */}
                        </div>
                        {/* <div className="grid grid-cols-2 gap-6 mt-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">User Name</label>
                                <input

                                    className="border p-2 w-full mb-2"
                                    style={{
                                        borderRadius: '24px', border: '1px solid #3483BE',
                                    }}
                                    disabled={true}
                                >

                                </input>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Password</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full p-2 pr-16 border rounded-full border-[#3483BE]"
                                        disabled
                                    />
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                                        <button
                                            onClick={handleCopy}
                                            className="text-gray-500 hover:text-gray-700 transition-colors"
                                            title={isCopied ? "Copied!" : "Copy password"}
                                        >
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/desktop/copy-svgrepo.svg`}
                                                className="h-5 w-5"
                                            />
                                        </button>
                                        <button
                                            onClick={handleGeneratePassword}
                                            className="text-gray-500 hover:text-gray-700 transition-colors"
                                            title="Generate new password"
                                        >
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/desktop/loading-svgrepo.svg`}
                                                className="h-5 w-5"
                                            />
                                        </button>
                                    </div>
                                </div>
                                {isCopied && (
                                    <div className="text-sm text-green-600 mt-1">
                                        Copied to clipboard!
                                    </div>
                                )}
                            </div>
                        </div> */}

                        <div className="mt-8">
                            <label className="block text-sm font-medium text-gray-700">Admin Notes</label>
                            <div className="relative">
                                <textarea
                                    placeholder="Admin Notes"
                                    value={newLead.adminNotes || ""}
                                    onChange={(e) =>
                                        setNewLead({ ...newLead, adminNotes: e.target.value })
                                    }
                                    className="border p-2 w-full mb-2"
                                    style={{ borderRadius: '24px', border: '1px solid #3483BE', }}

                                />
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-4">
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/editicon.svg`}
                                        alt="Edit Icon" className="w-5 h-5 cursor-pointer" onClick={() => console.log("Edit Admin Notes")} />
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`} alt="Delete Icon" className="w-5 h-5 cursor-pointer"
                                        onClick={() => setNewLead({ ...newLead, adminNotes: "" })} />
                                </div>
                            </div>
                        </div>
                        <div className="mt-8 flex justify-end">
                            <button onClick={editingId ? handleUpdateLead : handleAddLead} className="px-8 py-2 bg-gradient-to-b from-[#FFA782] to-[#FC583D] text-white rounded-3xl">    Submit    </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Action Modal */}
            {selectedAction && (
                <ActionModals
                    action={selectedAction ? Actions?.find((item) => item.id === selectedAction)?.name ?? "'" : "'"}
                    // selectAll={false} /* Not using global select all anymore */
                    count={selectedLeadsForAction.length}
                    selectedIds={selectedLeadsForAction}
                    // neglectedIds={[]}
                    // filterQuery={{
                    //     sourceId: selectedLeadSources[0],
                    //     stateId: selectedLeadStates[0],
                    //     stageId: selectedLeadStages[0],
                    //     statusId: selectedLeadStatuses[0],
                    //     admissionStatusId: selectedAdmissionStatuses[0],
                    //     welcomeCallByAdmissionTeamStatusId: selectedWelcomeCallByAdmissionTeamStatuses[0],
                    //     visaFileProcessingStatusId: selectedVisaProcessingStatuses[0],
                    //     applicationMovedStatusId: selectedApplicationMovedStatuses[0],
                    //     conditionalOfferLetterStatusId: selectedConditionalOfferLetterStatuses[0],
                    //     unconditionalOfferLetterStatusId: selectedUnConditionalOfferLetterStatuses[0],
                    //     feePaymentStatusId: selectedFeePaymentStatuses[0],
                    //     fileSubmissionStatusId: selectedFileSubmissionStatuses[0],
                    //     hasActiveFollowUp: selectedFollowUpStatuses[0] === 'true' ? true : selectedFollowUpStatuses[0] === 'false' ? false : null,
                    //     assignedTo: getAssignedToValue(),
                    //     campaignId: selectedCampaignOptions[0],
                    //     startDate: selectedStartDate.length > 0 ? convertToFirebaseTimestamp(selectedStartDate[0], true) : null,
                    //     endDate: selectedEndDate.length > 0 ? convertToFirebaseTimestamp(selectedEndDate[0], false) : null,
                    //     nameSearchQuery: searchQuery !== '' ? searchQuery : null,
                    //     phoneSearchQuery: phoneSearchQuery !== '' ? phoneSearchQuery : null,
                    // }}
                    onClose={(message) => handleOnClose(message)}
                />
            )}

            {/* Collaborator Modal */}
            {showCollaborateModal && selectedStudent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-96">
                        <h2 className="text-lg font-semibold mb-4">Add Collaborators</h2>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Student Name</label>
                            <div className="mt-1 p-2 bg-gray-50 rounded-md">
                                {selectedStudent.name}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                            <div className="mt-1 p-2 bg-gray-50 rounded-md">
                                {selectedStudentPhone}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Select Collaborators</label>
                            <div className="relative mt-1">
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                                    placeholder="Type 3 or more characters to search users..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setSearchTerm(value);
                                        handleSearchUsers(value);
                                    }}
                                />
                                {isLoadingUsers && (
                                    <div className="absolute right-2 top-2">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-600"></div>
                                    </div>
                                )}

                                {/* Dropdown of search results */}
                                {searchTerm.length >= 3 && searchResults.length > 0 && (
                                    <div className="absolute z-10 w-full mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                                        {searchResults
                                            .filter(user => !collaborators.includes(user.id))
                                            .map(user => (
                                                <div
                                                    key={user.id}
                                                    className="cursor-pointer hover:bg-gray-100 px-4 py-2"
                                                    onClick={() => {
                                                        handleSelectUser(user.id);
                                                        setSearchTerm('');
                                                    }}
                                                >
                                                    {user.name}
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>

                            {/* Selected users display */}
                            <div className="mt-2 flex flex-wrap gap-2">
                                {selectedUserDetails.map(user => (
                                    <div
                                        key={user.id}
                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                                    >
                                        {user.name}
                                        <button
                                            type="button"
                                            className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-cyan-600 hover:bg-cyan-200 hover:text-cyan-500 focus:outline-none"
                                            onClick={() => handleRemoveUser(user.id)}
                                        >
                                            <span className="sr-only">Remove {user.name}</span>
                                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowCollaborateModal(false);
                                    setCollaborators([]);
                                    setSelectedUserDetails([]);
                                    setSelectedStudent(null);
                                    setSearchTerm('');
                                    setSearchResults([]);
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCollaborateSubmit}
                                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Transfer Modal */}
            {showTransferModal && selectedStudent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-3xl w-[500px] max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-medium">Transfer Lead</h3>
                                <button onClick={() => setShowTransferModal(false)} className="text-gray-500 hover:text-gray-700">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="mb-6">
                                <p className="text-sm text-gray-600 mb-1">Student Name</p>
                                <p className="font-medium">{selectedStudent.name}</p>
                                <p className="text-sm text-gray-600 mt-3 mb-1">Phone Number</p>
                                <p className="font-medium">{selectedStudentPhone}</p>
                                {isAdmin && currentScreen === "Admin" && (
                                    <>
                                        <p className="text-sm text-gray-600 mt-3 mb-1">Current Relationship Manager</p>
                                        <p className="font-medium">{currentAssignedUser?.name || 'Not assigned'}</p>
                                    </>
                                )}
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm text-gray-600 mb-2">Search Users</label>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        handleSearchUsers(e.target.value);
                                    }}
                                    placeholder="Type to search users..."
                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                            </div>

                            {isLoadingUsers ? (
                                <div className="text-center py-4">Loading...</div>
                            ) : (
                                <div className="max-h-48 overflow-y-auto mb-6">
                                    {searchResults.map(user => (
                                        <div
                                            key={user.id}
                                            onClick={() => handleSelectTransferUser(user)}
                                            className={`p-3 rounded-lg cursor-pointer mb-2 ${selectedTransferUser?.id === user.id
                                                ? 'bg-cyan-100 border-2 border-cyan-500'
                                                : 'hover:bg-gray-100'
                                                }`}
                                        >
                                            {user.name}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowTransferModal(false)}
                                    className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleTransferSubmit}
                                    disabled={!selectedTransferUser}
                                    className={`px-6 py-2 text-white rounded-full ${selectedTransferUser
                                        ? 'bg-cyan-600 hover:bg-cyan-700'
                                        : 'bg-gray-400 cursor-not-allowed'
                                        }`}
                                >
                                    Transfer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};

// Convert date's time to start and end of the day
const convertToFirebaseTimestamp = (
    date: string | Date,
    isStartDate: boolean = true
): Timestamp => {
    const dateObj = new Date(date);

    if (isStartDate) {
        dateObj.setHours(5, 30, 0, 0);
    } else {
        dateObj.setDate(dateObj.getDate() + 1);
        dateObj.setHours(5, 29, 59, 999);
    }

    console.log('dateObj', dateObj);


    return Timestamp.fromDate(dateObj);
};

// const convertToFirebaseTimestamp = (
//     date: string | Date,
//     isStartDate: boolean = true
//   ): Timestamp => {
//     const dateObj = new Date(date);

//     if (isStartDate) {
//       // Set to start of day (00:00:00) in UTC+5:30
//       dateObj.setHours(0, 0, 0, 0);
//     } else {
//       // Set to end of day (23:59:59.999) in UTC+5:30
//       dateObj.setHours(23, 59, 59, 999);
//     }

//     console.log('dateObj', Timestamp.fromDate(dateObj));


//     // Convert to Firebase Timestamp
//     return Timestamp.fromDate(dateObj);
//   };

export default LeadManagement;